import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import usePlans from "../../hooks/usePlans";
import useSettings from "../../hooks/useSettings";
import LandingHeader from "./LandingHeader";
import LandingFooter from "./Footer";
import CookieBanner from "./CookieBanner";
import VLibras from "./VLibras";
import { getPublicLandingSections } from "../../services/landingSectionsService";
import { openApi } from "../../services/api";
import { ICON_MAP, DEFAULT_INTEG_CARDS, INTEGRATION_ICONS } from "./integrationIcons";

/* ─── CSS global do site ─── */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

  :root {
    --primary: #2563EB;
    --primary-dark: #1E40AF;
    --grad: linear-gradient(135deg, #2563EB 0%, #2563eb 100%);
    --grad-soft: linear-gradient(135deg, #E6F7FF 0%, #dbeafe 100%);
    --green: #14B8A6;
    --dark: #060818;
    --dark2: #0d1022;
    --text: #0d0f1c;
    --text2: #64748b;
    --text3: #94a3b8;
    --bg: #ffffff;
    --bg2: #f8f9fc;
    --border: #e8edf3;
    --shadow: 0 4px 24px rgba(0,0,0,.06);
    --shadow-lg: 0 16px 56px rgba(0,0,0,.12);
    --radius: 20px;
    --radius-sm: 12px;
  }

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", Roboto, sans-serif;
    background: var(--bg); color: var(--text);
    overflow-x: hidden; line-height: 1.6;
  }

  /* ── HERO ── */
  .lp-hero {
    padding: 0 40px;
    background: var(--dark);
    position: relative; overflow: hidden;
    min-height: 100vh;
    display: flex; align-items: center;
  }
  .lp-hero__mesh {
    position: absolute; inset: 0; z-index: 0; pointer-events: none;
    background:
      radial-gradient(ellipse 80% 70% at 50% 10%, rgba(37, 99, 235,.22) 0%, transparent 60%),
      radial-gradient(ellipse 60% 50% at 10% 80%, rgba(37,99,235,.18) 0%, transparent 55%),
      radial-gradient(ellipse 50% 60% at 90% 70%, rgba(37, 99, 235,.14) 0%, transparent 50%);
  }
  .lp-hero__grid {
    position: absolute; inset: 0; z-index: 0; pointer-events: none;
    background-image:
      linear-gradient(rgba(255,255,255,.022) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255,255,255,.022) 1px, transparent 1px);
    background-size: 60px 60px;
    mask-image: radial-gradient(ellipse 90% 75% at 50% 35%, black, transparent);
    -webkit-mask-image: radial-gradient(ellipse 90% 75% at 50% 35%, black, transparent);
  }
  /* Animated decorative orbs */
  .lp-hero__orb {
    position: absolute; border-radius: 50%; pointer-events: none; z-index: 0;
    filter: blur(90px);
  }
  .lp-hero__orb--1 {
    width: 600px; height: 600px;
    background: radial-gradient(circle, rgba(37, 99, 235,.32), transparent 70%);
    top: -160px; left: -140px;
    animation: lpOrbFloat 14s ease-in-out infinite;
  }
  .lp-hero__orb--2 {
    width: 480px; height: 480px;
    background: radial-gradient(circle, rgba(37,99,235,.24), transparent 70%);
    bottom: -100px; right: -120px;
    animation: lpOrbFloat 18s ease-in-out infinite reverse;
  }
  .lp-hero__orb--3 {
    width: 320px; height: 320px;
    background: radial-gradient(circle, rgba(236,72,153,.13), transparent 70%);
    top: 45%; right: 8%;
    animation: lpOrbFloat 11s ease-in-out infinite 4s;
  }
  @keyframes lpOrbFloat {
    0%, 100% { transform: translate(0, 0) scale(1); }
    33% { transform: translate(40px, -30px) scale(1.06); }
    66% { transform: translate(-25px, 40px) scale(0.96); }
  }
  .lp-hero__inner {
    max-width: 900px; margin: 0 auto; padding: 150px 0 130px;
    width: 100%;
    display: flex; flex-direction: column; align-items: center; text-align: center;
    position: relative; z-index: 1;
  }
  .lp-hero__mascote {
    width: 168px; max-width: 42%; height: auto; display: block;
    margin: 0 auto 20px;
    filter: drop-shadow(0 16px 40px rgba(37, 99, 235, .45));
    animation: lpFloat 4s ease-in-out infinite;
  }
  @keyframes lpFloat { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-12px); } }
  .lp-hero__eyebrow {
    display: inline-flex; align-items: center; gap: 8px;
    background: rgba(37, 99, 235,.14); border: 1px solid rgba(96, 165, 250,.35);
    border-radius: 100px; padding: 8px 20px;
    font-size: 12px; font-weight: 700; color: #93C5FD; margin-bottom: 32px;
    letter-spacing: .6px; text-transform: uppercase;
  }
  .lp-hero__eyebrow-dot {
    width: 6px; height: 6px; border-radius: 50%; background: #60A5FA;
    animation: lpPulse 2s infinite; flex-shrink: 0;
  }
  @keyframes lpPulse { 0%,100% { opacity:1; transform:scale(1); } 50% { opacity:.35; transform:scale(1.6); } }
  .lp-hero__title {
    font-size: clamp(40px, 5.5vw, 78px); font-weight: 900;
    color: #fff; line-height: 1.05; letter-spacing: -3px;
    margin-bottom: 28px; max-width: 880px;
  }
  .lp-hero__title-hi {
    display: inline;
    background: linear-gradient(90deg, #0EA5E9 0%, #60a5fa 35%, #60A5FA 65%, #0EA5E9 100%);
    background-size: 300% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: lpGradFlow 5s linear infinite;
  }
  @keyframes lpGradFlow {
    0% { background-position: 0% center; }
    100% { background-position: 300% center; }
  }
  .lp-hero__sub {
    font-size: clamp(16px, 1.6vw, 19px); color: rgba(255,255,255,.52);
    line-height: 1.78; margin-bottom: 48px; max-width: 600px;
  }
  .lp-hero__btns {
    display: flex; gap: 14px; flex-wrap: wrap; margin-bottom: 72px;
    justify-content: center;
  }
  .lp-hero__btn-main {
    background: var(--grad); color: #fff; border: none;
    padding: 18px 42px; border-radius: 14px;
    font-size: 16px; font-weight: 700; cursor: pointer;
    animation: lpGlow 3.5s ease-in-out infinite;
    transition: transform .2s, opacity .2s; font-family: inherit;
  }
  @keyframes lpGlow {
    0%, 100% { box-shadow: 0 0 30px rgba(37, 99, 235,.4), 0 6px 20px rgba(0,0,0,.22); }
    50% { box-shadow: 0 0 64px rgba(37, 99, 235,.72), 0 12px 32px rgba(0,0,0,.3); }
  }
  .lp-hero__btn-main:hover {
    opacity: .88; transform: translateY(-3px); animation: none;
    box-shadow: 0 0 72px rgba(37, 99, 235,.65), 0 14px 40px rgba(0,0,0,.35);
  }
  .lp-hero__btn-sec {
    background: rgba(255,255,255,.07); color: rgba(255,255,255,.88);
    border: 1px solid rgba(255,255,255,.18); padding: 18px 42px;
    border-radius: 14px; font-size: 16px; font-weight: 600;
    cursor: pointer; transition: background .2s, border-color .2s; font-family: inherit;
    backdrop-filter: blur(8px);
  }
  .lp-hero__btn-sec:hover { background: rgba(255,255,255,.13); border-color: rgba(255,255,255,.32); }
  .lp-hero__stats {
    display: flex; gap: 0; flex-wrap: wrap;
    background: rgba(255,255,255,.05);
    border: 1px solid rgba(255,255,255,.1);
    border-radius: 20px; padding: 26px 48px;
    backdrop-filter: blur(16px);
    justify-content: center;
  }
  .lp-hero__stat { padding: 0 36px; text-align: center; }
  .lp-hero__stat:first-child { padding-left: 0; }
  .lp-hero__stat:last-child { padding-right: 0; }
  .lp-hero__stat + .lp-hero__stat {
    padding-left: 36px;
    border-left: 1px solid rgba(255,255,255,.12);
  }
  .lp-hero__stat-val { font-size: 30px; font-weight: 800; color: #fff; line-height: 1; }
  .lp-hero__stat-val span { color: #60A5FA; }
  .lp-hero__stat-lbl { font-size: 12px; color: rgba(255,255,255,.38); margin-top: 6px; letter-spacing: .3px; }

  /* ── MARQUEE ── */
  .lp-marquee-wrap {
    padding: 20px 0; overflow: hidden;
    background: var(--mqbg, #080a18);
    border-top: 1px solid rgba(255,255,255,.06);
    border-bottom: 1px solid rgba(255,255,255,.06);
    position: relative;
  }
  .lp-marquee-wrap::before, .lp-marquee-wrap::after {
    content: ''; position: absolute; top: 0; bottom: 0; width: 120px; z-index: 2; pointer-events: none;
  }
  .lp-marquee-wrap::before { left: 0; background: linear-gradient(to right, var(--mqbg, #080a18), transparent); }
  .lp-marquee-wrap::after { right: 0; background: linear-gradient(to left, var(--mqbg, #080a18), transparent); }
  .lp-marquee { display: flex; gap: 0; animation: lpScroll 40s linear infinite; width: max-content; }
  .lp-marquee:hover { animation-play-state: paused; }
  @keyframes lpScroll { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
  .lp-marquee__item {
    display: flex; align-items: center; gap: 9px;
    padding: 0 32px; white-space: nowrap;
    font-size: 13px; font-weight: 600; color: var(--mqtext, rgba(255,255,255,.42));
    border-right: 1px solid rgba(255,255,255,.07);
    transition: color .2s;
  }
  .lp-marquee__item:hover { color: rgba(255,255,255,.72); }
  .lp-marquee__dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
  .lp-marquee__icon { font-size: 15px; line-height: 1; flex-shrink: 0; }

  /* ── SECTION ── */
  .lp-sec { padding: 104px 40px; }
  .lp-sec--bg { background: var(--bg2); }
  .lp-wrap { max-width: 1240px; margin: 0 auto; }

  /* ── SCROLL ANIMATIONS ── */
  .lp-anim { opacity: 0; transform: translateY(28px); transition: opacity .7s cubic-bezier(.16,1,.3,1), transform .7s cubic-bezier(.16,1,.3,1); }
  .lp-anim.lp-in { opacity: 1; transform: translateY(0); }
  .lp-anim[data-d="1"] { transition-delay: .07s; }
  .lp-anim[data-d="2"] { transition-delay: .14s; }
  .lp-anim[data-d="3"] { transition-delay: .22s; }
  .lp-anim[data-d="4"] { transition-delay: .30s; }
  .lp-anim[data-d="5"] { transition-delay: .38s; }

  /* ── SECTION SHARED ── */
  .lp-tag {
    display: inline-flex; align-items: center; gap: 7px;
    background: linear-gradient(135deg, rgba(37, 99, 235,.1) 0%, rgba(37,99,235,.07) 100%);
    border: 1px solid rgba(37, 99, 235,.22);
    color: #1D4ED8; border-radius: 100px;
    padding: 6px 18px; font-size: 11px; font-weight: 700; letter-spacing: .8px;
    text-transform: uppercase; margin-bottom: 18px;
    box-shadow: 0 0 28px rgba(37, 99, 235,.12);
  }
  .lp-h2 {
    font-size: clamp(30px, 3.6vw, 50px); font-weight: 900;
    color: #0d0f1c; line-height: 1.1; letter-spacing: -1.2px;
    text-align: center; margin-bottom: 16px;
  }
  .lp-h2--left { text-align: left; }
  .lp-sub {
    font-size: 17px; color: #64748b; text-align: center;
    max-width: 540px; margin: 0 auto 68px; line-height: 1.78; font-weight: 400;
  }
  .lp-sub--left { text-align: left; margin-left: 0; }

  /* Dark section variant */
  .lp-sec--dark { background: #04050e; }
  .lp-sec--dark .lp-tag {
    background: rgba(96, 165, 250,.14); border-color: rgba(96, 165, 250,.28);
    color: #60A5FA; box-shadow: 0 0 28px rgba(37, 99, 235,.18);
  }
  .lp-sec--dark .lp-h2 { color: #fff; }
  .lp-sec--dark .lp-sub { color: rgba(255,255,255,.5); }
  .lp-sec--dark .lp-integ-card {
    background: rgba(255,255,255,.04); border-color: rgba(255,255,255,.09);
  }
  .lp-sec--dark .lp-integ-card::before {
    background: linear-gradient(135deg, rgba(37, 99, 235,.15) 0%, rgba(37,99,235,.08) 100%);
  }
  .lp-sec--dark .lp-integ-card:hover {
    border-color: rgba(196,181,253,.5); background: rgba(255,255,255,.07);
    box-shadow: 0 0 0 1px rgba(196,181,253,.25), 0 24px 64px rgba(37, 99, 235,.22);
  }
  .lp-sec--dark .lp-integ-card h3 { color: #fff; }
  .lp-sec--dark .lp-integ-card p { color: rgba(255,255,255,.5); }
  .lp-sec--dark .lp-integ-link { color: #60A5FA; }

  /* ── FEATURE ALTERNATING ROWS ── */
  .lp-feat-section { }
  .lp-feat-row-wrap { padding: 100px 40px; position: relative; }
  .lp-feat-row-wrap--bg {
    background: linear-gradient(180deg, #f7f8fc 0%, #eff1f8 100%);
  }
  .lp-feat-row-wrap--bg::before {
    content: ''; position: absolute; inset: 0; pointer-events: none;
    background-image: radial-gradient(circle, rgba(37, 99, 235,.035) 1px, transparent 1px);
    background-size: 30px 30px;
  }
  .lp-feat-row {
    max-width: 1180px; margin: 0 auto;
    display: grid; grid-template-columns: 1fr 1fr; gap: 88px; align-items: center;
    position: relative; z-index: 1;
  }
  @media (max-width: 900px) {
    .lp-feat-row { grid-template-columns: 1fr; gap: 48px; }
    .lp-feat-row--rev .lp-feat-row__visual { order: -1; }
  }
  .lp-feat-row--rev .lp-feat-row__visual { order: -1; }
  .lp-feat-row__title {
    font-size: clamp(28px, 3vw, 42px); font-weight: 900;
    color: #0d0f1c; line-height: 1.1; letter-spacing: -.8px;
    margin: 14px 0 20px;
  }
  .lp-feat-row__hi { color: #2563EB; }
  .lp-feat-row__hi--g { color: #14B8A6; }
  .lp-feat-row__hi--b { color: #2563eb; }
  .lp-feat-row__desc { font-size: 16px; color: #64748b; line-height: 1.78; margin-bottom: 28px; }
  .lp-feat-row__list { list-style: none; display: flex; flex-direction: column; gap: 14px; margin-bottom: 36px; }
  .lp-feat-row__list li {
    display: flex; align-items: center; gap: 12px;
    font-size: 15px; color: #374151; font-weight: 500;
  }
  .lp-feat-row__check {
    width: 24px; height: 24px; border-radius: 8px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
    background: linear-gradient(135deg, rgba(37, 99, 235,.12), rgba(37,99,235,.07));
    border: 1px solid rgba(37, 99, 235,.18);
  }
  .lp-feat-row__btn {
    display: inline-flex; align-items: center; gap: 9px;
    font-size: 14px; font-weight: 700; color: #2563EB;
    background: rgba(37, 99, 235,.08); border: 1px solid rgba(37, 99, 235,.22);
    cursor: pointer; padding: 11px 22px; border-radius: 12px;
    transition: all .2s; font-family: inherit;
  }
  .lp-feat-row__btn:hover {
    background: rgba(37, 99, 235,.14); border-color: rgba(37, 99, 235,.4);
    gap: 14px; box-shadow: 0 4px 16px rgba(37, 99, 235,.15);
  }

  /* UI MOCKUPS */
  .lp-mock {
    background: #fff; border-radius: 24px;
    border: 1px solid #e8edf3;
    box-shadow: 0 8px 24px rgba(0,0,0,.04), 0 36px 88px rgba(0,0,0,.1);
    overflow: hidden;
    transition: transform .4s cubic-bezier(.16,1,.3,1), box-shadow .4s;
  }
  .lp-feat-row__visual:hover .lp-mock {
    transform: translateY(-8px) scale(1.015);
    box-shadow: 0 12px 32px rgba(0,0,0,.05), 0 52px 100px rgba(37, 99, 235,.1);
  }
  .lp-mock__bar {
    background: linear-gradient(135deg, #f7f8fc 0%, #f1f3f8 100%);
    border-bottom: 1px solid #e8edf3; padding: 13px 16px;
    display: flex; align-items: center; gap: 7px;
  }
  .lp-mock__dot { width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0; }
  .lp-mock__title { font-size: 12px; font-weight: 600; color: #94a3b8; margin-left: 8px; }
  .lp-mock__body { padding: 20px; }
  /* campaign rows */
  .lp-mock-camp { display: flex; align-items: center; gap: 12px; padding: 12px 0; border-bottom: 1px solid #f5f7fa; }
  .lp-mock-camp:last-of-type { border-bottom: none; }
  .lp-mock-camp__av { width: 36px; height: 36px; border-radius: 11px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; }
  .lp-mock-camp__info { flex: 1; min-width: 0; }
  .lp-mock-camp__name { font-size: 12px; font-weight: 700; color: #0d0f1c; margin-bottom: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .lp-mock-camp__status { font-size: 10px; color: #94a3b8; }
  .lp-mock-camp__right { display: flex; flex-direction: column; align-items: flex-end; gap: 4px; flex-shrink: 0; }
  .lp-mock-camp__pct { font-size: 11px; font-weight: 800; }
  .lp-mock-prog { width: 72px; height: 5px; background: #f0f2f5; border-radius: 3px; }
  .lp-mock-prog__fill { height: 100%; border-radius: 3px; }
  .lp-mock-stats { display: flex; border-top: 1px solid #f0f2f5; margin-top: 16px; padding-top: 16px; }
  .lp-mock-stat { flex: 1; text-align: center; }
  .lp-mock-stat:not(:last-child) { border-right: 1px solid #f0f2f5; }
  .lp-mock-stat__val { font-size: 19px; font-weight: 900; color: #0d0f1c; }
  .lp-mock-stat__lbl { font-size: 10px; color: #94a3b8; margin-top: 2px; }
  /* kanban mini */
  .lp-mock-kb { display: flex; gap: 10px; padding: 16px; }
  .lp-mock-kb__col { flex: 1; min-width: 0; }
  .lp-mock-kb__h { font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: .6px; margin-bottom: 8px; }
  .lp-mock-kb__card { background: #fff; border-radius: 10px; padding: 9px; margin-bottom: 7px; border: 1px solid #e8edf3; box-shadow: 0 2px 8px rgba(0,0,0,.06); }
  .lp-mock-kb__card-n { font-size: 11px; font-weight: 700; color: #0d0f1c; margin-bottom: 4px; }
  .lp-mock-kb__tag { display: inline-block; font-size: 9px; padding: 2px 7px; border-radius: 100px; font-weight: 700; }
  .lp-mock-kb__count { font-size: 9px; color: #94a3b8; margin-top: 6px; }
  /* chat ui */
  .lp-mock-chat { display: flex; flex-direction: column; gap: 10px; }
  .lp-mock-chat__header { display: flex; align-items: center; gap: 10px; padding-bottom: 14px; border-bottom: 1px solid #f0f2f5; margin-bottom: 4px; }
  .lp-mock-chat__hav { width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700; color: #fff; flex-shrink: 0; }
  .lp-mock-chat__hname { font-size: 13px; font-weight: 700; color: #0d0f1c; }
  .lp-mock-chat__hsub { font-size: 10px; color: #94a3b8; }
  .lp-mock-chat__badge { margin-left: auto; font-size: 9px; font-weight: 700; padding: 3px 9px; border-radius: 100px; background: #E6F7FF; color: #0F766E; }
  .lp-mock-msg { display: flex; gap: 7px; }
  .lp-mock-msg--r { flex-direction: row-reverse; }
  .lp-mock-msg__av { width: 26px; height: 26px; border-radius: 50%; flex-shrink: 0; display: flex; align-items: center; justify-content: center; font-size: 9px; font-weight: 700; color: #fff; margin-top: 2px; }
  .lp-mock-msg__bub { padding: 9px 13px; border-radius: 14px; font-size: 11px; line-height: 1.5; max-width: 75%; }
  .lp-mock-msg__bub--in { background: #f3f4f6; color: #374151; border-bottom-left-radius: 3px; }
  .lp-mock-msg__bub--out { background: linear-gradient(135deg, #2563EB, #2563eb); color: #fff; border-bottom-right-radius: 3px; }
  .lp-mock-msg__time { font-size: 9px; color: #d1d5db; margin-top: 3px; }

  /* ── HOW IT WORKS ── */
  .lp-how { display: grid; grid-template-columns: 1fr 1fr; gap: 80px; align-items: center; }
  @media (max-width: 860px) { .lp-how { grid-template-columns: 1fr; gap: 48px; } }
  .lp-step { display: flex; gap: 20px; }
  .lp-step__left { display: flex; flex-direction: column; align-items: center; flex-shrink: 0; }
  .lp-step__num {
    width: 46px; height: 46px; border-radius: 50%;
    background: linear-gradient(135deg, #2563EB 0%, #2563eb 100%);
    box-shadow: 0 6px 20px rgba(37, 99, 235,.4);
    display: flex; align-items: center; justify-content: center;
    font-size: 13px; font-weight: 900; color: #fff; flex-shrink: 0;
  }
  .lp-step__connector {
    width: 2px; flex: 1;
    background: linear-gradient(to bottom, rgba(37, 99, 235,.5), transparent);
    min-height: 20px; margin: 8px 0;
  }
  .lp-step:last-child .lp-step__connector { display: none; }
  .lp-step__body { padding: 8px 0 36px; }
  .lp-step:last-child .lp-step__body { padding-bottom: 0; }
  .lp-step__title { font-size: 17px; font-weight: 800; color: #0d0f1c; margin-bottom: 8px; }
  .lp-step__desc { font-size: 14px; color: #64748b; line-height: 1.72; }
  .lp-how__visual {
    background: #fff; border-radius: 24px;
    border: 1px solid #e8edf3; overflow: hidden;
    box-shadow: 0 8px 24px rgba(0,0,0,.04), 0 32px 80px rgba(0,0,0,.1);
  }
  .lp-how__visual-top {
    background: linear-gradient(135deg, #2563EB 0%, #2563eb 100%);
    padding: 14px 18px; display: flex; align-items: center; gap: 10px;
  }
  .lp-how__visual-title { color: rgba(255,255,255,.9); font-size: 13px; font-weight: 600; margin: 0 auto; }
  .lp-kanban { display: flex; gap: 10px; padding: 18px; overflow: hidden; }
  .lp-kanban__col { flex: 1; min-width: 0; }
  .lp-kanban__col-h { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .6px; margin-bottom: 10px; }
  .lp-kanban__card {
    background: #fff; border-radius: 12px; padding: 10px; margin-bottom: 8px;
    box-shadow: 0 3px 10px rgba(0,0,0,.07); border: 1px solid #e8edf3;
  }
  .lp-kanban__card-n { font-size: 11px; font-weight: 700; color: #0d0f1c; margin-bottom: 5px; }
  .lp-kanban__card-t { display: inline-block; font-size: 9px; padding: 2px 7px; border-radius: 100px; font-weight: 700; }

  /* ── INTEGRATIONS ── */
  .lp-integ-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 18px; }
  @media (max-width: 960px) { .lp-integ-grid { grid-template-columns: repeat(2, 1fr); } }
  @media (max-width: 540px) { .lp-integ-grid { grid-template-columns: 1fr; } }
  .lp-integ-card {
    background: #fff; border: 1px solid #e8edf3; border-radius: 22px;
    padding: 32px 26px; display: flex; flex-direction: column;
    transition: border-color .3s, box-shadow .3s, transform .3s, background .3s;
    cursor: default; position: relative; overflow: hidden;
  }
  .lp-integ-card::before {
    content: ''; position: absolute; inset: 0;
    background: linear-gradient(135deg, rgba(37, 99, 235,.07) 0%, rgba(37,99,235,.04) 100%);
    opacity: 0; transition: opacity .3s;
  }
  .lp-integ-card:hover::before { opacity: 1; }
  .lp-integ-card:hover {
    border-color: #93C5FD;
    box-shadow: 0 0 0 1px rgba(196,181,253,.3), 0 20px 56px rgba(37, 99, 235,.14), 0 4px 12px rgba(0,0,0,.05);
    transform: translateY(-7px);
  }
  .lp-integ-icon { width: 56px; height: 56px; border-radius: 16px; margin-bottom: 20px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .lp-integ-card h3 { font-size: 15px; font-weight: 700; color: #0d0f1c; margin-bottom: 8px; }
  .lp-integ-card p { font-size: 13px; color: #64748b; line-height: 1.65; margin-bottom: 16px; flex: 1; }
  .lp-integ-link { display: inline-flex; align-items: center; gap: 5px; font-size: 13px; font-weight: 700; color: #2563EB; background: none; border: none; cursor: pointer; padding: 0; transition: gap .2s; }
  .lp-integ-link:hover { gap: 9px; }

  /* ── TESTIMONIALS ── */
  .lp-testi-section { text-align: center; }
  .lp-testi-grid {
    display: grid; grid-template-columns: repeat(3, 1fr); gap: 22px;
    max-width: 1100px; margin: 52px auto 0; text-align: left;
  }
  @media (max-width: 900px) { .lp-testi-grid { grid-template-columns: 1fr; max-width: 560px; } }
  .lp-testi-card {
    background: #fff; border-radius: 22px; padding: 36px 32px;
    box-shadow: 0 4px 8px rgba(0,0,0,.03), 0 16px 40px rgba(37, 99, 235,.07);
    border: 1px solid #E6F7FF; position: relative; overflow: hidden;
    transition: transform .35s cubic-bezier(.16,1,.3,1), box-shadow .35s, border-color .3s;
  }
  .lp-testi-card::before {
    content: ''; position: absolute; top: 0; left: 0; right: 0; height: 4px;
    background: linear-gradient(90deg, #2563EB, #2563eb);
    border-radius: 22px 22px 0 0;
  }
  .lp-testi-card:hover {
    transform: translateY(-6px);
    box-shadow: 0 8px 20px rgba(0,0,0,.05), 0 28px 64px rgba(37, 99, 235,.14);
  }
  .lp-testi-card--active {
    border-color: #93C5FD;
    box-shadow: 0 0 0 2px rgba(37, 99, 235,.22), 0 24px 60px rgba(37, 99, 235,.18);
  }
  .lp-testi-quote-mark { font-size: 56px; color: #E6F7FF; line-height: 1; margin-bottom: -4px; font-family: Georgia, serif; display: block; }
  .lp-testi-stars { color: #f59e0b; font-size: 14px; letter-spacing: 2px; margin-bottom: 14px; }
  .lp-testi-quote { font-size: 15px; color: #1e293b; line-height: 1.78; margin-bottom: 28px; font-style: italic; }
  .lp-testi-author { display: flex; align-items: center; gap: 12px; }
  .lp-testi-av { width: 44px; height: 44px; border-radius: 50%; flex-shrink: 0; display: flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 700; color: #fff; }
  .lp-testi-name { font-size: 14px; font-weight: 700; color: #0d0f1c; }
  .lp-testi-role { font-size: 12px; color: #94a3b8; margin-top: 2px; }
  .lp-testi-dots { display: flex; gap: 8px; align-items: center; justify-content: center; margin-top: 40px; }
  .lp-testi-dot { width: 8px; height: 8px; border-radius: 50%; background: #ddd6fe; border: none; cursor: pointer; padding: 0; transition: background .25s, transform .25s, width .25s; }
  .lp-testi-dot--active { background: #2563EB; transform: scale(1.5); }
  @keyframes lpSlideIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
  .lp-testi-card { animation: lpSlideIn .4s ease; }

  /* ── PRICING ── */
  .lp-price-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 20px; max-width: 1200px; margin: 0 auto; }
  @media (max-width: 1040px) { .lp-price-grid { grid-template-columns: repeat(2, 1fr); max-width: 620px; } }
  @media (max-width: 640px) { .lp-price-grid { grid-template-columns: 1fr; max-width: 360px; } }
  .lp-price-card {
    background: #fff; border-radius: 24px; padding: 40px 32px;
    border: 1px solid #e8edf3; position: relative;
    box-shadow: 0 2px 4px rgba(0,0,0,.03), 0 8px 24px rgba(0,0,0,.04);
    transition: box-shadow .3s, transform .3s;
  }
  .lp-price-card:hover { transform: translateY(-5px); box-shadow: 0 8px 16px rgba(0,0,0,.05), 0 24px 56px rgba(0,0,0,.09); }
  .lp-price-card--feat {
    background: linear-gradient(150deg, #2563EB 0%, #2563eb 100%);
    border-color: transparent;
    box-shadow: 0 12px 40px rgba(37, 99, 235,.35), 0 28px 72px rgba(37, 99, 235,.18);
    overflow: hidden;
  }
  .lp-price-card--feat::after {
    content: ''; position: absolute; top: 0; left: -100%;
    width: 60%; height: 100%; pointer-events: none;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,.12), transparent);
    animation: lpShimmerCard 4s ease-in-out infinite 1s;
  }
  @keyframes lpShimmerCard { 0% { left: -100%; } 60%,100% { left: 200%; } }
  .lp-price-card--feat:hover { transform: translateY(-8px); box-shadow: 0 20px 56px rgba(37, 99, 235,.45), 0 36px 90px rgba(37, 99, 235,.22); }
  .lp-price-badge {
    display: block; text-align: center; margin-bottom: 20px;
    background: #0d0f1c; color: #fff; font-size: 10px; font-weight: 700;
    padding: 5px 20px; border-radius: 100px; white-space: nowrap;
    letter-spacing: 1px; text-transform: uppercase;
    width: fit-content; margin-left: auto; margin-right: auto;
  }
  .lp-price-name { font-size: 15px; font-weight: 800; margin-bottom: 4px; color: #0d0f1c; }
  .lp-price-card--feat .lp-price-name { color: rgba(255,255,255,.95); }
  .lp-price-tagline { font-size: 13px; margin-bottom: 24px; color: #64748b; }
  .lp-price-card--feat .lp-price-tagline { color: rgba(255,255,255,.6); }
  .lp-price-amount { font-size: 44px; font-weight: 800; line-height: 1; letter-spacing: -1.5px; color: #0d0f1c; }
  .lp-price-card--feat .lp-price-amount { color: #fff; }
  .lp-price-suffix { font-size: 14px; font-weight: 400; color: #94a3b8; }
  .lp-price-card--feat .lp-price-suffix { color: rgba(255,255,255,.6); }
  .lp-price-div { border: none; border-top: 1px solid #e8edf3; margin: 28px 0; }
  .lp-price-card--feat .lp-price-div { border-color: rgba(255,255,255,.15); }
  .lp-price-items { list-style: none; display: flex; flex-direction: column; gap: 11px; margin-bottom: 32px; }
  .lp-price-item { display: flex; align-items: center; gap: 10px; font-size: 14px; color: #374151; }
  .lp-price-item--on-w { color: rgba(255,255,255,.9); }
  .lp-price-item--off { color: #d1d5db; }
  .lp-price-btn { width: 100%; padding: 14px; border-radius: 13px; font-size: 14px; font-weight: 700; cursor: pointer; transition: all .2s; font-family: inherit; }
  .lp-price-btn--primary { background: linear-gradient(135deg, #2563EB, #2563eb); color: #fff; border: none; box-shadow: 0 4px 16px rgba(37, 99, 235,.28); }
  .lp-price-btn--primary:hover { box-shadow: 0 8px 28px rgba(37, 99, 235,.45); transform: translateY(-1px); }
  .lp-price-btn--white { background: rgba(255,255,255,.18); color: #fff; border: 1px solid rgba(255,255,255,.3); }
  .lp-price-btn--white:hover { background: rgba(255,255,255,.28); }
  .lp-price-btn--outline { background: none; color: #2563EB; border: 1.5px solid #ddd6fe; }
  .lp-price-btn--outline:hover { background: #f5f3ff; border-color: #93C5FD; }

  /* ── BLOG ── */
  .lp-blog { background: #fff; }
  .lp-blog__carousel-wrap { position: relative; margin-top: 52px; padding: 0 30px; }
  .lp-blog__viewport { overflow: hidden; }
  .lp-blog__track { display: flex; gap: 24px; width: 100%; transition: transform 0.45s cubic-bezier(0.25, 0.46, 0.45, 0.94); }
  .lp-blog__card { flex: 0 0 calc(33.333% - 16px); border: 1px solid #e8edf3; border-radius: 18px; overflow: hidden; transition: box-shadow .25s, transform .25s; background: #fff; cursor: pointer; }
  .lp-blog__card:hover { box-shadow: 0 16px 48px rgba(0,0,0,.1); transform: translateY(-5px); }
  .lp-blog__img { width: 100%; height: 210px; object-fit: cover; display: block; }
  .lp-blog__img--placeholder { width: 100%; height: 210px; background: linear-gradient(135deg, #E6F7FF 0%, #dbeafe 100%); display: block; }
  .lp-blog__body { padding: 22px; }
  .lp-blog__date { font-size: 11px; color: #94a3b8; margin-bottom: 8px; font-weight: 500; text-transform: uppercase; letter-spacing: .5px; }
  .lp-blog__title { font-size: 16px; font-weight: 700; color: #0d0f1c; margin-bottom: 8px; line-height: 1.4; }
  .lp-blog__resumo { font-size: 13px; color: #64748b; line-height: 1.65; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
  .lp-blog__arrow { position: absolute; top: calc(50% - 28px); transform: translateY(-50%); width: 46px; height: 46px; border-radius: 50%; border: 1px solid #e8edf3; background: #fff; box-shadow: 0 4px 18px rgba(0,0,0,.1); cursor: pointer; z-index: 2; display: flex; align-items: center; justify-content: center; color: #2563EB; transition: background .2s, box-shadow .2s, color .2s; padding: 0; }
  .lp-blog__arrow:hover { background: #2563EB; color: #fff; box-shadow: 0 6px 24px rgba(37, 99, 235,.35); border-color: transparent; }
  .lp-blog__arrow:disabled { opacity: 0.2; cursor: default; pointer-events: none; }
  .lp-blog__arrow--prev { left: -6px; }
  .lp-blog__arrow--next { right: -6px; }
  .lp-blog__dots { display: flex; justify-content: center; gap: 8px; margin-top: 32px; }
  .lp-blog__dot { width: 8px; height: 8px; border-radius: 50%; border: none; background: #e2e8f0; cursor: pointer; padding: 0; transition: background .25s, transform .25s; }
  .lp-blog__dot--active { background: #2563EB; transform: scale(1.5); }
  @media (max-width: 1023px) { .lp-blog__card { flex: 0 0 calc(50% - 12px); } }
  @media (max-width: 639px) { .lp-blog__card { flex: 0 0 100%; } .lp-blog__carousel-wrap { padding: 0 18px; } }

  /* ── FAQ ── */
  .lp-faq-list { max-width: 740px; margin: 0 auto; display: flex; flex-direction: column; gap: 12px; }
  .lp-faq-item {
    background: #fff; border: 1px solid #e8edf3; border-radius: 18px;
    overflow: hidden; transition: border-color .25s, box-shadow .25s, transform .25s;
  }
  .lp-faq-item--open {
    border-color: #93C5FD;
    box-shadow: 0 0 0 1px rgba(196,181,253,.25), 0 8px 32px rgba(37, 99, 235,.1);
    transform: scale(1.005);
  }
  .lp-faq-btn {
    width: 100%; background: none; border: none; cursor: pointer; text-align: left;
    padding: 22px 28px; display: flex; justify-content: space-between;
    align-items: center; gap: 16px; font-size: 15px; font-weight: 700;
    color: #0d0f1c; font-family: inherit;
    transition: background .15s;
  }
  .lp-faq-btn:hover { background: rgba(37, 99, 235,.025); }
  .lp-faq-body {
    padding: 0 28px 22px; font-size: 15px; color: #64748b; line-height: 1.78;
    border-top: 1px solid #f1f3f8; padding-top: 18px; margin: 0 16px;
  }
  .lp-faq-chev { transition: transform .3s cubic-bezier(.16,1,.3,1); color: #94a3b8; flex-shrink: 0; }
  .lp-faq-chev--open { transform: rotate(180deg); color: #2563EB; }

  /* ── CTA ── */
  .lp-cta {
    background: linear-gradient(140deg, #3b0764 0%, #1e3a8a 100%);
    padding: 120px 40px; text-align: center; position: relative; overflow: hidden;
  }
  .lp-cta::before {
    content: ''; position: absolute; inset: 0; pointer-events: none;
    background:
      radial-gradient(ellipse 80% 90% at 20% 60%, rgba(96, 165, 250,.2), transparent),
      radial-gradient(ellipse 60% 80% at 80% 20%, rgba(96,165,250,.15), transparent),
      radial-gradient(ellipse 40% 50% at 50% 100%, rgba(236,72,153,.08), transparent);
  }
  .lp-cta::after {
    content: ''; position: absolute; top: 0; left: -100%;
    width: 60%; height: 100%; pointer-events: none;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,.04), transparent);
    animation: lpShimmer 6s ease-in-out infinite 2s;
  }
  @keyframes lpShimmer { 0% { left: -100%; } 100% { left: 200%; } }
  .lp-cta__inner { max-width: 640px; margin: 0 auto; position: relative; z-index: 1; }
  .lp-cta__eyebrow {
    display: inline-block; background: rgba(255,255,255,.12); color: rgba(255,255,255,.9);
    padding: 7px 20px; border-radius: 100px; font-size: 11px; font-weight: 700;
    letter-spacing: 1px; text-transform: uppercase; margin-bottom: 30px;
    border: 1px solid rgba(255,255,255,.2);
  }
  .lp-cta__title { font-size: clamp(32px, 4.2vw, 54px); font-weight: 900; color: #fff; letter-spacing: -1px; line-height: 1.1; margin-bottom: 20px; }
  .lp-cta__sub { font-size: 18px; color: rgba(255,255,255,.65); margin-bottom: 48px; line-height: 1.72; }
  .lp-cta__btn {
    display: inline-flex; align-items: center; gap: 10px;
    background: #fff; color: #1E40AF; border: none;
    padding: 20px 52px; border-radius: 16px; font-size: 17px; font-weight: 800;
    cursor: pointer; font-family: inherit;
    box-shadow: 0 12px 48px rgba(0,0,0,.22), 0 0 0 1px rgba(255,255,255,.1);
    transition: transform .25s, box-shadow .25s;
  }
  .lp-cta__btn:hover { transform: translateY(-3px); box-shadow: 0 20px 64px rgba(0,0,0,.32), 0 0 80px rgba(255,255,255,.1); }
  .lp-cta__trust { display: flex; gap: 36px; justify-content: center; flex-wrap: wrap; margin-top: 40px; }
  .lp-cta__trust-item { display: flex; align-items: center; gap: 8px; font-size: 14px; font-weight: 500; color: rgba(255,255,255,.68); }
  .lp-cta__trust-dot { width: 20px; height: 20px; background: rgba(255,255,255,.18); border-radius: 50%; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }

  /* ── MODAL ── */
  .lp-modal-overlay {
    position: fixed; inset: 0; z-index: 2000;
    background: rgba(8,8,20,.8);
    backdrop-filter: blur(8px);
    display: flex; align-items: center; justify-content: center;
    padding: 20px; animation: lpFadeIn .2s ease;
  }
  @keyframes lpFadeIn { from { opacity: 0; } to { opacity: 1; } }
  .lp-modal {
    background: #fff; border-radius: 28px;
    padding: 0; max-width: 560px; width: 100%;
    box-shadow: 0 40px 100px rgba(0,0,0,.28);
    animation: lpSlideUp .28s ease;
    overflow: hidden; position: relative;
  }
  @keyframes lpSlideUp { from { opacity: 0; transform: translateY(28px); } to { opacity: 1; transform: translateY(0); } }
  .lp-modal__header {
    padding: 32px 32px 24px;
    display: flex; align-items: center; gap: 18px;
    border-bottom: 1px solid var(--border);
  }
  .lp-modal__icon-wrap {
    width: 64px; height: 64px; border-radius: 18px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
  }
  .lp-modal__title { font-size: 22px; font-weight: 900; color: var(--text); margin-bottom: 4px; }
  .lp-modal__subtitle { font-size: 14px; color: var(--text2); }
  .lp-modal__close {
    position: absolute; top: 16px; right: 16px;
    width: 32px; height: 32px; border-radius: 50%;
    background: var(--bg2); border: none; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    color: var(--text3); transition: background .2s;
  }
  .lp-modal__close:hover { background: var(--border); }
  .lp-modal__body { padding: 28px 32px; }
  .lp-modal__desc { font-size: 15px; color: var(--text2); line-height: 1.65; margin-bottom: 24px; }
  .lp-modal__features { list-style: none; display: flex; flex-direction: column; gap: 12px; margin-bottom: 32px; }
  .lp-modal__feature { display: flex; align-items: flex-start; gap: 12px; }
  .lp-modal__feat-check { width: 22px; height: 22px; border-radius: 8px; flex-shrink: 0; display: flex; align-items: center; justify-content: center; margin-top: 1px; }
  .lp-modal__feat-text { font-size: 14px; color: var(--text); line-height: 1.5; }
  .lp-modal__feat-text strong { color: var(--text); font-weight: 700; }
  .lp-modal__footer { padding: 0 32px 28px; display: flex; gap: 10px; }
  .lp-modal__cta { flex: 1; padding: 13px; border-radius: var(--radius-sm); background: var(--grad); color: #fff; border: none; font-size: 15px; font-weight: 700; cursor: pointer; transition: opacity .2s; box-shadow: 0 4px 16px rgba(37, 99, 235,.3); font-family: inherit; }
  .lp-modal__cta:hover { opacity: .88; }
  .lp-modal__sec { padding: 13px 20px; border-radius: var(--radius-sm); background: none; border: 1.5px solid var(--border); color: var(--text2); font-size: 14px; font-weight: 600; cursor: pointer; transition: border-color .2s; font-family: inherit; }
  .lp-modal__sec:hover { border-color: var(--primary); color: var(--primary); }

  /* ── WA FLOAT ── */
  .lp-wa {
    position: fixed; bottom: 28px; right: 28px; z-index: 999;
    width: 58px; height: 58px; border-radius: 50%;
    background: #14B8A6; display: flex; align-items: center; justify-content: center;
    box-shadow: 0 8px 32px rgba(20, 184, 166,.45), 0 4px 12px rgba(0,0,0,.15);
    text-decoration: none; transition: transform .25s, box-shadow .25s;
  }
  .lp-wa:hover { transform: scale(1.1); box-shadow: 0 12px 44px rgba(20, 184, 166,.6), 0 6px 16px rgba(0,0,0,.2); }

  /* ── RESPONSIVE ── */
  @media (max-width: 860px) {
    .lp-hero { padding: 0 24px; }
    .lp-hero__inner { padding: 40px 0 44px; }
    .lp-sec { padding: 72px 24px; }
    .lp-feat-row-wrap { padding: 72px 24px; }
  }
  @media (max-width: 640px) {
    .lp-hero__mascote { width: 118px; margin-bottom: 10px; }
    .lp-hero__title { letter-spacing: -2px; }
    .lp-hero__stats { padding: 20px 28px; gap: 0; }
    .lp-hero__stat { padding: 12px 0; width: 50%; border-left: none !important; }
    .lp-hero__stat:nth-child(odd) { border-right: 1px solid rgba(255,255,255,.12); padding-right: 16px; }
    .lp-hero__stat:nth-child(even) { padding-left: 16px; }
    .lp-hero__stat:nth-child(1), .lp-hero__stat:nth-child(2) { border-bottom: 1px solid rgba(255,255,255,.08); }
    .lp-hero__btns { flex-direction: column; gap: 12px; }
    .lp-hero__btn-main, .lp-hero__btn-sec { width: 100%; }
  }
`;

const CheckDark = ({ color = "#2563EB" }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const Arr = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
    <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
  </svg>
);

// Quando vem um afiliado, os botoes de compra apontam para o checkout dele.
// Sem afiliado, a pagina se comporta exatamente como sempre.
export default function LandingPage({ afiliado = null }) {
  const linksAfiliado = {};
  if (afiliado?.links) {
    afiliado.links.forEach(l => {
      linksAfiliado[String(l.planId)] = l.url;
    });
  }

  // Botao de um plano especifico.
  const comprarPlano = plan => {
    const meu = linksAfiliado[String(plan.id)];
    if (meu) {
      window.location.href = meu;
      return;
    }
    if (afiliado) return; // afiliado sem link para este plano: nao faz nada
    history.push({ pathname: "/cadastro", search: `?planId=${plan.id}` });
  };

  // Botoes gerais do topo e do rodape: sem plano escolhido, leva para a
  // secao de precos em vez de mandar para o nosso cadastro.
  const irParaPlanos = () => {
    if (afiliado) {
      const alvo = document.getElementById("pricing");
      if (alvo) alvo.scrollIntoView({ behavior: "smooth" });
      return;
    }
    window.location.href = "https://app.floow.chat/cadastro";
  };

  const history = useHistory();
  const { getPlanList } = usePlans();
  const { getPublicSetting } = useSettings();
  const [plans, setPlans] = useState([]);
  const [appName, setAppName] = useState("");
  const [openFaq, setOpenFaq] = useState(null);
  const [secData, setSecData] = useState({});
  const previewDataRef = useRef(null);
  const [testiIdx, setTestiIdx] = useState(0);
  const [blogPosts, setBlogPosts] = useState([]);
  const [blogIdx, setBlogIdx] = useState(0);
  const [pageReady, setPageReady] = useState(false);
  const [blogVisible, setBlogVisible] = useState(3);
  const blogMaxIdx = Math.max(0, blogPosts.length - blogVisible);
  const blogDragX = useRef(null);
  const blogDragged = useRef(false);

  const blogTransform = (() => {
    if (blogIdx === 0) return 'translateX(0)';
    const pct = (blogIdx * 100 / blogVisible).toFixed(4);
    const px  = (blogIdx * 24  / blogVisible).toFixed(4);
    return `translateX(calc(-${pct}% - ${px}px))`;
  })();

  // s(sectionKey, field, default) — retorna valor salvo (inclusive "" para limpar) ou fallback
  const s = (sectionKey, field, def) => {
    const val = secData[sectionKey]?.[field];
    return (val !== undefined && val !== null) ? val : def;
  };

  // Numero do WhatsApp (botao flutuante + CTAs) - editavel no Super Admin > Landing > Contato
  const waNumber = String(s("contact", "whatsapp", "552433540335") || "552433540335").replace(/\D/g, "");

  useEffect(() => {
    // A pagina do afiliado usa esta mesma tela e precisa abrir nos dois
    // dominios; so a nossa landing e que desvia para o login aqui.
    if (!afiliado && window.location.hostname === "app.floow.chat") {
      window.location.replace("https://app.floow.chat/login");
    }
  }, [afiliado]);

  useEffect(() => {
    getPlanList().then(d => { if (d?.length) setPlans(d); }).catch(() => {});
    getPublicSetting("appName").then(v => { if (v) setAppName(v); }).catch(() => {});
    getPublicLandingSections().then(data => {
      if (data) setSecData(previewDataRef.current ? { ...data, ...previewDataRef.current } : data);
      setPageReady(true);
    }).catch(() => { setPageReady(true); });
    openApi.get("/open/blog-posts").then(r => { if (Array.isArray(r.data) && r.data.length) setBlogPosts(r.data); }).catch(() => {});
  }, []);

  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      const v = w < 640 ? 1 : w < 1024 ? 2 : 3;
      setBlogVisible(v);
      setBlogIdx(i => Math.min(i, Math.max(0, blogPosts.length - v)));
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [blogPosts.length]);

  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add("lp-in"); }),
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );
    document.querySelectorAll(".lp-anim").forEach(el => io.observe(el));
    return () => io.disconnect();
  }, [plans]);

  useEffect(() => {
    const timer = setInterval(() => {
      setTestiIdx(prev => {
        const total = (secData.testimonials?.items?.length || 3);
        return (prev + 1) % total;
      });
    }, 5000);
    return () => clearInterval(timer);
  }, [secData]);

  /* ── Listener para preview em tempo real do admin ── */
  useEffect(() => {
    const handler = (e) => {
      if (!e.data || typeof e.data !== "object") return;
      if (e.data.type === "LP_PREVIEW_UPDATE" && e.data.data) {
        previewDataRef.current = e.data.data;
        setSecData(prev => ({ ...prev, ...e.data.data }));
      }
      if (e.data.type === "LP_SCROLL_TO" && e.data.anchor) {
        const el = document.getElementById(e.data.anchor);
        if (el) el.scrollIntoView({ behavior: "smooth" });
        else window.scrollTo({ top: 0, behavior: "smooth" });
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  const scroll = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const steps = [
    { num: "01", title: s("how_it_works", "s1t", "Captação"), desc: s("how_it_works", "s1d", "Aborde visitantes com saudações automáticas via chatbot. Transforme conversas em oportunidades de venda.") },
    { num: "02", title: s("how_it_works", "s2t", "Qualificação"), desc: s("how_it_works", "s2d", "Conduza o lead pelo funil com perguntas e ofertas personalizadas baseadas no perfil de cada contato.") },
    { num: "03", title: s("how_it_works", "s3t", "Atendimento"), desc: s("how_it_works", "s3d", "Transfira leads qualificados para seus agentes em tempo real e reduza o ciclo de atendimento.") },
    { num: "04", title: s("how_it_works", "s4t", "Conversão"), desc: s("how_it_works", "s4d", "Deixe clientes comprarem, agendarem ou tirarem dúvidas com IA disponível 24h por dia.") },
  ];

  const integrations = (() => {
    const saved = secData.integrations_cards;
    const cards = (saved && saved.cards && saved.cards.length >= 3) ? saved.cards : DEFAULT_INTEG_CARDS;
    return cards.map(card => {
      const ic = ICON_MAP[card.iconKey] || INTEGRATION_ICONS[0];
      const icon = (card.iconType === "upload" && card.iconImage)
        ? <img src={`${process.env.REACT_APP_BACKEND_URL}/public/landing/${card.iconImage}`} alt={card.title} style={{ width: 32, height: 32, objectFit: "contain", display: "block" }} />
        : ic.render(card.iconColor);
      return {
        id: card.id,
        bg: card.iconBg || "",
        icon,
        title: card.title,
        desc: card.desc,
        cardBg: card.cardBg || "",
        cardBorder: card.cardBorder || "",
        titleColor: card.titleColor || "",
        descColor: card.descColor || "",
      };
    });
  })();

  const testimonials = secData.testimonials?.items?.length
    ? secData.testimonials.items.map(t => ({
        stars: "★★★★★",
        quote: t.quote || "",
        name: t.name || "",
        role: t.role || "",
        color: t.color || "#2563EB",
        photo: t.photo || "",
        init: (t.name || "?").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase(),
      }))
    : [
        { stars: "★★★★★", quote: s("testimonials", "t1q", "Transformamos completamente nosso atendimento! Triplicamos a capacidade sem aumentar a equipe."), name: s("testimonials", "t1n", "Mariana Costa"), role: s("testimonials", "t1r", "Gestora de Operações"), color: "#2563EB", init: "MC", photo: "" },
        { stars: "★★★★★", quote: s("testimonials", "t2q", "Centralizamos tudo: atendimento, vendas e campanhas. Nosso tempo de resposta caiu 70% e as conversões subiram."), name: s("testimonials", "t2n", "Ricardo Alves"), role: s("testimonials", "t2r", "Diretor Comercial"), color: "#2563eb", init: "RA", photo: "" },
        { stars: "★★★★★", quote: s("testimonials", "t3q", "O chatbot atende 24h fora do horário comercial. Nossa equipe foca nos casos que realmente precisam de atenção humana."), name: s("testimonials", "t3n", "Fernanda Lima"), role: s("testimonials", "t3r", "Proprietária"), color: "#14B8A6", init: "FL", photo: "" },
      ];

  const faqs = secData.faq?.items?.length
    ? secData.faq.items.map(f => ({ q: f.q || "", a: f.a || "" }))
    : [
        { q: s("faq", "q1", "Quantos atendentes posso ter?"), a: s("faq", "a1", "Depende do plano. Temos opções para times de 2 até operações com dezenas de agentes — e tudo escala conforme você cresce.") },
        { q: s("faq", "q2", "A IA responde automaticamente pelos meus clientes?"), a: s("faq", "a2", "Sim. Configure o prompt, escolha o modelo (GPT-4, Claude e outros), e a IA assume o atendimento com memória por contato e transfere para humano quando necessário.") },
        { q: s("faq", "q3", "Quais canais de comunicação são suportados?"), a: s("faq", "a3", "WhatsApp via Baileys e API Oficial Meta, Facebook Messenger, Instagram Direct, e-mail e chat via API REST.") },
        { q: s("faq", "q4", "Posso integrar com meu sistema atual?"), a: s("faq", "a4", "Sim. API REST completa e documentada, webhooks por evento e integração nativa com N8N, Typebot, Google Agenda e mais.") },
        { q: s("faq", "q5", "Como funciona o período de teste?"), a: s("faq", "a5", "Entre em contato com nosso time via WhatsApp para iniciar um trial guiado e personalizado para o seu negócio.") },
        { q: s("faq", "q6", "Posso trocar de plano depois?"), a: s("faq", "a6", "Sim, você pode evoluir de plano a qualquer momento sem perder histórico ou configurações.") },
      ];

  /* ── Hover overrides dinâmicos do admin ── */
  const hoverCSS = [
    /* Features — marketing */
    s("features_marketing","btnHoverBgColor","")     && `#feat-marketing .lp-feat-row__btn:hover { background: ${s("features_marketing","btnHoverBgColor","")} !important; }`,
    s("features_marketing","btnHoverBorderColor","") && `#feat-marketing .lp-feat-row__btn:hover { border-color: ${s("features_marketing","btnHoverBorderColor","")} !important; }`,
    s("features_marketing","btnHoverTextColor","")   && `#feat-marketing .lp-feat-row__btn:hover { color: ${s("features_marketing","btnHoverTextColor","")} !important; }`,
    /* Features — vendas */
    s("features_vendas","btnHoverBgColor","")     && `#feat-vendas .lp-feat-row__btn:hover { background: ${s("features_vendas","btnHoverBgColor","")} !important; }`,
    s("features_vendas","btnHoverBorderColor","") && `#feat-vendas .lp-feat-row__btn:hover { border-color: ${s("features_vendas","btnHoverBorderColor","")} !important; }`,
    s("features_vendas","btnHoverTextColor","")   && `#feat-vendas .lp-feat-row__btn:hover { color: ${s("features_vendas","btnHoverTextColor","")} !important; }`,
    /* Features — suporte */
    s("features_suporte","btnHoverBgColor","")     && `#feat-suporte .lp-feat-row__btn:hover { background: ${s("features_suporte","btnHoverBgColor","")} !important; }`,
    s("features_suporte","btnHoverBorderColor","") && `#feat-suporte .lp-feat-row__btn:hover { border-color: ${s("features_suporte","btnHoverBorderColor","")} !important; }`,
    s("features_suporte","btnHoverTextColor","")   && `#feat-suporte .lp-feat-row__btn:hover { color: ${s("features_suporte","btnHoverTextColor","")} !important; }`,
    /* CTA */
    s("cta","btnHoverBgColor","")   && `.lp-cta__btn:hover { background: ${s("cta","btnHoverBgColor","")} !important; }`,
    s("cta","btnHoverTextColor","") && `.lp-cta__btn:hover { color: ${s("cta","btnHoverTextColor","")} !important; }`,
    /* Blog arrows */
    s("blog","arrowHoverBg","")    && `.lp-blog__arrow:hover { background: ${s("blog","arrowHoverBg","")} !important; border-color: transparent !important; }`,
    s("blog","arrowHoverColor","") && `.lp-blog__arrow:hover { color: ${s("blog","arrowHoverColor","")} !important; }`,
  ].filter(Boolean).join("\n");

  return (
    <div style={{ opacity: pageReady ? 1 : 0, transition: "opacity 0.3s ease" }}>
      <style>{CSS}</style>
      {hoverCSS && <style>{hoverCSS}</style>}

      <LandingHeader scrollTo={scroll} />

      {/* ── HERO ── */}
      <section id="hero" className="lp-hero" style={{ background: s("hero", "bgColor", "#060818") }}>
        {s("hero", "image", "") && (
          <img
            src={`${process.env.REACT_APP_BACKEND_URL}/public/landing/${s("hero", "image", "")}`}
            alt=""
            aria-hidden="true"
            style={{
              position: "absolute", inset: 0,
              width: "100%", height: "100%",
              objectFit: "cover", objectPosition: "center",
              opacity: parseFloat(s("hero", "bgOpacity", "0.3")),
              zIndex: 0, display: "block", pointerEvents: "none",
            }}
          />
        )}
        <div className="lp-hero__mesh" />
        <div className="lp-hero__grid" />
        <div className="lp-hero__orb lp-hero__orb--1" aria-hidden="true" />
        <div className="lp-hero__orb lp-hero__orb--2" aria-hidden="true" />
        <div className="lp-hero__orb lp-hero__orb--3" aria-hidden="true" />
        <div className="lp-hero__inner">
            <img
              src={`${process.env.REACT_APP_BACKEND_URL}/public/floowchat-mascote.png`}
              alt="Floow Chat"
              className="lp-hero__mascote"
            />
            {s("hero", "eyebrow", "Plataforma Omnichannel com IA") && (
              <div
                className="lp-hero__eyebrow"
                style={{
                  ...(s("hero", "eyebrowColor", "") ? { color: s("hero", "eyebrowColor", "") } : {}),
                  ...(s("hero", "eyebrowBgColor", "") ? {
                    background: s("hero", "eyebrowBgColor", ""),
                    borderColor: s("hero", "eyebrowBgColor", ""),
                  } : {}),
                }}
              >
                <span
                  className="lp-hero__eyebrow-dot"
                  style={s("hero", "eyebrowColor", "") ? { background: s("hero", "eyebrowColor", "") } : undefined}
                />
                {s("hero", "eyebrow", "Plataforma Omnichannel com IA")}
              </div>
            )}
            <h1 className="lp-hero__title" style={s("hero", "textColor", "") ? { color: s("hero", "textColor", "") } : undefined}>
              {s("hero", "title", "Atendimento no WhatsApp")}<br />
              <span
                className="lp-hero__title-hi"
                style={s("hero", "titleHiColor", "") ? {
                  color: s("hero", "titleHiColor", ""),
                  WebkitTextFillColor: s("hero", "titleHiColor", ""),
                  background: "none",
                  animation: "none",
                } : undefined}
              >{s("hero", "titleHi", "inteligente e escalável")}</span>
            </h1>
            <p className="lp-hero__sub">
              {s("hero", "sub", "Centralize canais, automatize com IA e gerencie toda sua equipe em um único painel. Sem complexidade, sem custo de infraestrutura.")}
            </p>
            <div className="lp-hero__btns">
              <button
                className="lp-hero__btn-main"
                style={{
                  ...(s("hero", "btnTextColor", "") ? { color: s("hero", "btnTextColor", "") } : {}),
                  ...(s("hero", "btnBgFrom", "") || s("hero", "btnBgTo", "") ? {
                    background: `linear-gradient(135deg, ${s("hero", "btnBgFrom", "#2563EB")} 0%, ${s("hero", "btnBgTo", "#2563eb")} 100%)`
                  } : {}),
                }}
                onClick={irParaPlanos}
              >
                {s("hero", "btnPrimary", "Criar conta gratuita →")}
              </button>
              <button
                className="lp-hero__btn-sec"
                style={{
                  ...(s("hero", "btn2TextColor", "") ? { color: s("hero", "btn2TextColor", "") } : {}),
                  ...(s("hero", "btn2BgColor", "") ? { background: s("hero", "btn2BgColor", ""), borderColor: s("hero", "btn2BgColor", "") } : {}),
                }}
                onClick={() => {
                  const link = s("hero", "btn2Link", "");
                  if (link) {
                    if (link.startsWith("http") || link.startsWith("//")) window.open(link, "_blank");
                    else history.push(link);
                  } else {
                    window.open("https://wa.me/" + waNumber, "_blank");
                  }
                }}
              >
                {s("hero", "btn2Text", "Ver demonstração")}
              </button>
            </div>
            <div className="lp-hero__stats">
              {[
                { val: s("hero", "stat1Val", "+500"), label: s("hero", "stat1Label", "Empresas ativas") },
                { val: s("hero", "stat2Val", "+2M"), label: s("hero", "stat2Label", "Mensagens/mês") },
                { val: s("hero", "stat3Val", "99,9%"), label: s("hero", "stat3Label", "Disponibilidade") },
                { val: s("hero", "stat4Val", "4,9★"), label: s("hero", "stat4Label", "Satisfação") },
              ].map((st, i) => (
                <div key={i} className="lp-hero__stat">
                  <div className="lp-hero__stat-val">
                    <span style={s("hero", "statValueColor", "") ? { color: s("hero", "statValueColor", "") } : undefined}>{st.val}</span>
                  </div>
                  <div className="lp-hero__stat-lbl" style={s("hero", "statLabelColor", "") ? { color: s("hero", "statLabelColor", "") } : undefined}>
                    {st.label}
                  </div>
                </div>
              ))}
            </div>
        </div>
      </section>

      {/* ── MARQUEE ── */}
      {(() => {
        const DEFAULTS = [
          { label: "WhatsApp Business",      color: "#14B8A6" },
          { label: "Instagram Direct",        color: "#E4405F" },
          { label: "Facebook Messenger",      color: "#1877F2" },
          { label: "Agente de IA GPT-4",      color: "#60A5FA" },
          { label: "Kanban de Leads",         color: "#f59e0b" },
          { label: "Campanhas em Massa",      color: "#ec4899" },
          { label: "API & Webhooks",          color: "#38bdf8" },
          { label: "Relatórios em Tempo Real",color: "#34d399" },
          { label: "Multi Atendimento",       color: "#f97316" },
          { label: "Chatbot Builder",         color: "#2563EB" },
          { label: "Agendamentos",            color: "#0EA5E9" },
          { label: "N8N & Typebot",           color: "#2563EB" },
        ];
        const items = DEFAULTS.map((def, idx) => {
          const n = idx + 1;
          const label = s("marquee", `item${n}label`, def.label);
          const color = s("marquee", `item${n}color`, def.color);
          const icon  = s("marquee", `item${n}icon`,  "");
          return label ? { label, color, icon } : null;
        }).filter(Boolean);
        const doubled = [...items, ...items];
        const mqbg   = s("marquee", "bgColor",   "#080a18");
        const mqtext = s("marquee", "textColor",  "");
        return (
          <div
            id="marquee"
            className="lp-marquee-wrap"
            style={{
              "--mqbg":   mqbg || "#080a18",
              "--mqtext": mqtext || "rgba(255,255,255,.42)",
            }}
          >
            <div className="lp-marquee">
              {doubled.map((item, i) => (
                <div key={i} className="lp-marquee__item">
                  {item.icon
                    ? <span className="lp-marquee__icon">{item.icon}</span>
                    : <span className="lp-marquee__dot" style={{ background: item.color }} />
                  }
                  {item.label}
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* ── FEATURES ── */}
      <div id="features" className="lp-feat-section">

        {/* 1 — Marketing */}
        <div id="feat-marketing" className="lp-feat-row-wrap">
          <div className="lp-feat-row lp-anim">
            <div className="lp-feat-row__text">
              <div className="lp-tag" style={s("features_marketing", "accentColor", "") ? { background: s("features_marketing", "accentColor", "") + "22", color: s("features_marketing", "accentColor", "") } : undefined}>{s("features_marketing", "tag", "Marketing & Campanhas")}</div>
              <h2 className="lp-feat-row__title" style={s("features_marketing", "textColor", "") ? { color: s("features_marketing", "textColor", "") } : undefined}>
                {s("features_marketing", "title", "Alcance clientes no")}<br />
                <span className="lp-feat-row__hi" style={{ color: s("features_marketing", "titleHiColor", "") || s("features_marketing", "accentColor", "#2563EB") }}>{s("features_marketing", "titleHi", "momento certo")}</span>
              </h2>
              <p className="lp-feat-row__desc">
                {s("features_marketing", "desc", "Dispare campanhas personalizadas via WhatsApp com textos gerados por IA para cada contato. Controle velocidade, agende horários e acompanhe resultados em tempo real.")}
              </p>
              <ul className="lp-feat-row__list">
                {[
                  s("features_marketing", "b1", "Campanhas segmentadas por tag, perfil ou histórico"),
                  s("features_marketing", "b2", "IA cria texto único para cada contato"),
                  s("features_marketing", "b3", "Relatório de entrega, leitura e resposta em tempo real"),
                ].map((item, i) => (
                  <li key={i}><span className="lp-feat-row__check"><CheckDark color={s("features_marketing", "accentColor", "#2563EB")} /></span> {item}</li>
                ))}
              </ul>
              <button
                className="lp-feat-row__btn"
                style={{
                  color: s("features_marketing", "btnColor", "") || s("features_marketing", "accentColor", "#2563EB") || "#2563EB",
                  ...(s("features_marketing", "btnBgColor", "") ? { background: s("features_marketing", "btnBgColor", ""), padding: "10px 20px", borderRadius: 8, border: "none" } : {}),
                }}
                onMouseEnter={e => {
                  const acc = s("features_marketing", "accentColor", "#2563EB");
                  e.currentTarget.style.background = s("features_marketing", "btnHoverBgColor", "") || `${acc}23`;
                  e.currentTarget.style.borderColor = s("features_marketing", "btnHoverBorderColor", "") || `${acc}66`;
                  e.currentTarget.style.color       = s("features_marketing", "btnHoverTextColor", "") || s("features_marketing", "btnColor", "") || acc;
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background  = s("features_marketing", "btnBgColor", "") || "";
                  e.currentTarget.style.borderColor = "";
                  e.currentTarget.style.color       = s("features_marketing", "btnColor", "") || s("features_marketing", "accentColor", "#2563EB");
                }}
                onClick={() => history.push(s("features_marketing", "btnLink", "/funcionalidades/marketing"))}
              >
                {s("features_marketing", "btnText", "Ver funcionalidade")} <Arr />
              </button>
            </div>
            <div className="lp-feat-row__visual">
              {s("features_marketing", "image", "") ? (
                <img src={`${process.env.REACT_APP_BACKEND_URL}/public/landing/${s("features_marketing", "image", "")}`} alt="Marketing" style={{ width: "100%", display: "block" }} />
              ) : (
              <div className="lp-mock">
                <div className="lp-mock__bar">
                  <div className="lp-mock__dot" style={{ background: "#ff5f57" }} />
                  <div className="lp-mock__dot" style={{ background: "#febc2e" }} />
                  <div className="lp-mock__dot" style={{ background: "#28c840" }} />
                  <span className="lp-mock__title">Campanhas — Atualizado agora</span>
                </div>
                <div className="lp-mock__body">
                  {[
                    { name: "Black Friday — Promoção Relâmpago", status: "Enviando", pct: 72, color: "#2563EB", bg: "#EFF6FF" },
                    { name: "Reativação de Clientes Inativos", status: "Agendada para 18h", pct: 0, color: "#f59e0b", bg: "#fef3c7" },
                    { name: "Boas-vindas — Novos Cadastros", status: "Concluída", pct: 100, color: "#14B8A6", bg: "#E6F7FF" },
                  ].map((c, i) => (
                    <div key={i} className="lp-mock-camp">
                      <div className="lp-mock-camp__av" style={{ background: c.bg }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={c.color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                      </div>
                      <div className="lp-mock-camp__info">
                        <div className="lp-mock-camp__name">{c.name}</div>
                        <div className="lp-mock-camp__status">{c.status}</div>
                      </div>
                      <div className="lp-mock-camp__right">
                        <span className="lp-mock-camp__pct" style={{ color: c.color }}>{c.pct}%</span>
                        <div className="lp-mock-prog"><div className="lp-mock-prog__fill" style={{ width: c.pct + "%", background: c.color }} /></div>
                      </div>
                    </div>
                  ))}
                  <div className="lp-mock-stats">
                    {[{ val: "12.483", lbl: "Enviadas" }, { val: "89%", lbl: "Entregues" }, { val: "34%", lbl: "Respondidas" }].map((s, i) => (
                      <div key={i} className="lp-mock-stat">
                        <div className="lp-mock-stat__val">{s.val}</div>
                        <div className="lp-mock-stat__lbl">{s.lbl}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              )}
            </div>
          </div>
        </div>

        {/* 2 — Vendas (invertido) */}
        <div id="feat-vendas" className="lp-feat-row-wrap lp-feat-row-wrap--bg">
          <div className="lp-feat-row lp-feat-row--rev lp-anim">
            <div className="lp-feat-row__text">
              <div className="lp-tag" style={s("features_vendas", "accentColor", "") ? { background: s("features_vendas", "accentColor", "") + "22", color: s("features_vendas", "accentColor", "") } : undefined}>{s("features_vendas", "tag", "Vendas & CRM")}</div>
              <h2 className="lp-feat-row__title" style={s("features_vendas", "textColor", "") ? { color: s("features_vendas", "textColor", "") } : undefined}>
                {s("features_vendas", "title", "Feche mais negócios")}<br />
                <span className="lp-feat-row__hi--g" style={{ color: s("features_vendas", "titleHiColor", "") || s("features_vendas", "accentColor", "#14B8A6") }}>{s("features_vendas", "titleHi", "sem perder leads")}</span>
              </h2>
              <p className="lp-feat-row__desc">
                {s("features_vendas", "desc", "Gerencie seu pipeline em um Kanban visual com drag-and-drop. Qualifique leads automaticamente e transfira para o vendedor certo com histórico completo.")}
              </p>
              <ul className="lp-feat-row__list">
                {[
                  s("features_vendas", "b1", "Kanban drag-and-drop com etapas personalizadas"),
                  s("features_vendas", "b2", "Qualificação automática via chatbot integrado"),
                  s("features_vendas", "b3", "Histórico completo de mensagens, notas e arquivos"),
                ].map((item, i) => (
                  <li key={i}><span className="lp-feat-row__check"><CheckDark color={s("features_vendas", "accentColor", "#14B8A6")} /></span> {item}</li>
                ))}
              </ul>
              <button
                className="lp-feat-row__btn"
                style={{
                  color: s("features_vendas", "btnColor", "") || s("features_vendas", "accentColor", "#14B8A6") || "#14B8A6",
                  ...(s("features_vendas", "btnBgColor", "") ? { background: s("features_vendas", "btnBgColor", ""), padding: "10px 20px", borderRadius: 8, border: "none" } : {}),
                }}
                onMouseEnter={e => {
                  const acc = s("features_vendas", "accentColor", "#14B8A6");
                  e.currentTarget.style.background = s("features_vendas", "btnHoverBgColor", "") || `${acc}23`;
                  e.currentTarget.style.borderColor = s("features_vendas", "btnHoverBorderColor", "") || `${acc}66`;
                  e.currentTarget.style.color       = s("features_vendas", "btnHoverTextColor", "") || s("features_vendas", "btnColor", "") || acc;
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background  = s("features_vendas", "btnBgColor", "") || "";
                  e.currentTarget.style.borderColor = "";
                  e.currentTarget.style.color       = s("features_vendas", "btnColor", "") || s("features_vendas", "accentColor", "#14B8A6");
                }}
                onClick={() => history.push(s("features_vendas", "btnLink", "/funcionalidades/vendas"))}
              >
                {s("features_vendas", "btnText", "Ver funcionalidade")} <Arr />
              </button>
            </div>
            <div className="lp-feat-row__visual">
              {s("features_vendas", "image", "") ? (
                <img src={`${process.env.REACT_APP_BACKEND_URL}/public/landing/${s("features_vendas", "image", "")}`} alt="Vendas" style={{ width: "100%", display: "block" }} />
              ) : (
              <div className="lp-mock">
                <div className="lp-mock__bar">
                  <div className="lp-mock__dot" style={{ background: "#ff5f57" }} />
                  <div className="lp-mock__dot" style={{ background: "#febc2e" }} />
                  <div className="lp-mock__dot" style={{ background: "#28c840" }} />
                  <span className="lp-mock__title">Pipeline CRM — 24 leads ativos</span>
                </div>
                <div className="lp-mock-kb">
                  {[
                    { h: "Novo", color: "#60a5fa", cards: [{ n: "Bruno F.", t: "WhatsApp", tc: "#dbeafe", tct: "#1d4ed8" }, { n: "Laura M.", t: "Orgânico", tc: "#E6F7FF", tct: "#0F766E" }], count: "7 leads" },
                    { h: "Qualificado", color: "#60A5FA", cards: [{ n: "Felipe S.", t: "Pro", tc: "#E6F7FF", tct: "#1D4ED8" }], count: "5 leads" },
                    { h: "Proposta", color: "#f59e0b", cards: [{ n: "Carla R.", t: "Urgente", tc: "#fef3c7", tct: "#92400e" }], count: "3 leads" },
                    { h: "Fechado", color: "#34d399", cards: [{ n: "Daniel W.", t: "Pago", tc: "#d1fae5", tct: "#065f46" }], count: "9 leads" },
                  ].map((col, ci) => (
                    <div key={ci} className="lp-mock-kb__col">
                      <div className="lp-mock-kb__h" style={{ color: col.color }}>{col.h}</div>
                      {col.cards.map((card, cj) => (
                        <div key={cj} className="lp-mock-kb__card">
                          <div className="lp-mock-kb__card-n">{card.n}</div>
                          <div className="lp-mock-kb__tag" style={{ background: card.tc, color: card.tct }}>{card.t}</div>
                        </div>
                      ))}
                      <div className="lp-mock-kb__count">{col.count}</div>
                    </div>
                  ))}
                </div>
              </div>
              )}
            </div>
          </div>
        </div>

        {/* 3 — Suporte */}
        <div id="feat-suporte" className="lp-feat-row-wrap">
          <div className="lp-feat-row lp-anim">
            <div className="lp-feat-row__text">
              <div className="lp-tag" style={s("features_suporte", "accentColor", "") ? { background: s("features_suporte", "accentColor", "") + "22", color: s("features_suporte", "accentColor", "") } : undefined}>{s("features_suporte", "tag", "Suporte & Atendimento")}</div>
              <h2 className="lp-feat-row__title" style={s("features_suporte", "textColor", "") ? { color: s("features_suporte", "textColor", "") } : undefined}>
                {s("features_suporte", "title", "Atenda mais rápido")}<br />
                <span className="lp-feat-row__hi--b" style={{ color: s("features_suporte", "titleHiColor", "") || s("features_suporte", "accentColor", "#2563eb") }}>{s("features_suporte", "titleHi", "com menos esforço")}</span>
              </h2>
              <p className="lp-feat-row__desc">
                {s("features_suporte", "desc", "Multiagente em tempo real, filas inteligentes, chatbot 24h e transferência com histórico completo. Todos os canais em um único painel.")}
              </p>
              <ul className="lp-feat-row__list">
                {[
                  s("features_suporte", "b1", "Vários agentes simultâneos em um único número"),
                  s("features_suporte", "b2", "Chatbot builder visual sem programar"),
                  s("features_suporte", "b3", "SLA, filas e transferência com contexto completo"),
                ].map((item, i) => (
                  <li key={i}><span className="lp-feat-row__check"><CheckDark color={s("features_suporte", "accentColor", "#2563eb")} /></span> {item}</li>
                ))}
              </ul>
              <button
                className="lp-feat-row__btn"
                style={{
                  color: s("features_suporte", "btnColor", "") || s("features_suporte", "accentColor", "#2563eb") || "#2563eb",
                  ...(s("features_suporte", "btnBgColor", "") ? { background: s("features_suporte", "btnBgColor", ""), padding: "10px 20px", borderRadius: 8, border: "none" } : {}),
                }}
                onMouseEnter={e => {
                  const acc = s("features_suporte", "accentColor", "#2563eb");
                  e.currentTarget.style.background = s("features_suporte", "btnHoverBgColor", "") || `${acc}23`;
                  e.currentTarget.style.borderColor = s("features_suporte", "btnHoverBorderColor", "") || `${acc}66`;
                  e.currentTarget.style.color       = s("features_suporte", "btnHoverTextColor", "") || s("features_suporte", "btnColor", "") || acc;
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background  = s("features_suporte", "btnBgColor", "") || "";
                  e.currentTarget.style.borderColor = "";
                  e.currentTarget.style.color       = s("features_suporte", "btnColor", "") || s("features_suporte", "accentColor", "#2563eb");
                }}
                onClick={() => history.push(s("features_suporte", "btnLink", "/funcionalidades/suporte"))}
              >
                {s("features_suporte", "btnText", "Ver funcionalidade")} <Arr />
              </button>
            </div>
            <div className="lp-feat-row__visual">
              {s("features_suporte", "image", "") ? (
                <img src={`${process.env.REACT_APP_BACKEND_URL}/public/landing/${s("features_suporte", "image", "")}`} alt="Suporte" style={{ width: "100%", display: "block" }} />
              ) : (
              <div className="lp-mock">
                <div className="lp-mock__bar">
                  <div className="lp-mock__dot" style={{ background: "#ff5f57" }} />
                  <div className="lp-mock__dot" style={{ background: "#febc2e" }} />
                  <div className="lp-mock__dot" style={{ background: "#28c840" }} />
                  <span className="lp-mock__title">Atendimento em tempo real</span>
                </div>
                <div className="lp-mock__body">
                  <div className="lp-mock-chat">
                    <div className="lp-mock-chat__header">
                      <div className="lp-mock-chat__hav" style={{ background: "linear-gradient(135deg,#2563EB,#2563eb)" }}>AM</div>
                      <div>
                        <div className="lp-mock-chat__hname">Ana Martins</div>
                        <div className="lp-mock-chat__hsub">WhatsApp · Ticket #4821</div>
                      </div>
                      <div className="lp-mock-chat__badge">Online</div>
                    </div>
                    {[
                      { av: "AM", bg: "#2563EB", msg: "Olá! Meu pedido ainda não chegou, já faz 5 dias.", side: "in" },
                      { av: "IA", bg: "#2563eb", msg: "Entendido! Consultei seu pedido #8842 e está a caminho. Previsão: amanhã até 18h.", side: "out" },
                      { av: "AM", bg: "#2563EB", msg: "Perfeito, obrigada! Posso mudar o endereço ainda?", side: "in" },
                      { av: "AG", bg: "#14B8A6", msg: "Sim! Vou transferir para nossa equipe de logística agora.", side: "out" },
                    ].map((m, i) => (
                      <div key={i} className={`lp-mock-msg${m.side === "out" ? " lp-mock-msg--r" : ""}`}>
                        <div className="lp-mock-msg__av" style={{ background: m.bg }}>{m.av}</div>
                        <div className={`lp-mock-msg__bub lp-mock-msg__bub--${m.side}`}>{m.msg}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              )}
            </div>
          </div>
        </div>

      </div>

      {/* ── HOW IT WORKS ── */}
      <section id="how" className="lp-sec lp-sec--bg" style={s("how_it_works", "bgColor", "") ? { background: s("how_it_works", "bgColor", "") } : undefined}>
        <div className="lp-wrap">
          <div className="lp-anim" style={{ textAlign: "center" }}>
            <div className="lp-tag" style={{
              ...(s("how_it_works", "tagBgColor", "") ? { background: s("how_it_works", "tagBgColor", ""), borderColor: s("how_it_works", "tagBgColor", "") } : {}),
              ...(s("how_it_works", "tagColor", "") ? { color: s("how_it_works", "tagColor", "") } : {}),
            }}>{s("how_it_works", "tag", "Como funciona")}</div>
          </div>
          <h2 className="lp-h2 lp-anim" data-d="1" style={s("how_it_works", "textColor", "") ? { color: s("how_it_works", "textColor", "") } : undefined}>{s("how_it_works", "title", "Crie experiências que convertem")}</h2>
          <p className="lp-sub lp-anim" data-d="2">{s("how_it_works", "sub", "Do primeiro contato ao fechamento — totalmente automatizado.")}</p>
          <div className="lp-how">
            <div className="lp-anim" data-d="2">
              {steps.map((step, i) => (
                <div key={i} className="lp-step">
                  <div className="lp-step__left">
                    <div className="lp-step__num" style={{
                      ...(s("how_it_works", "stepNumBgColor", "") ? { background: s("how_it_works", "stepNumBgColor", "") } : {}),
                      color: s("how_it_works", "stepNumColor", "#2563EB"),
                    }}>{step.num}</div>
                    <div className="lp-step__connector" style={s("how_it_works", "stepConnectorColor", "") ? { background: s("how_it_works", "stepConnectorColor", "") } : undefined} />
                  </div>
                  <div className="lp-step__body">
                    <div className="lp-step__title" style={s("how_it_works", "stepTitleColor", "") ? { color: s("how_it_works", "stepTitleColor", "") } : undefined}>{step.title}</div>
                    <div className="lp-step__desc" style={s("how_it_works", "stepDescColor", "") ? { color: s("how_it_works", "stepDescColor", "") } : undefined}>{step.desc}</div>
                  </div>
                </div>
              ))}
            </div>
            {s("how_it_works", "image", "") ? (
              <div className="lp-anim" data-d="3">
                <img
                  src={`${process.env.REACT_APP_BACKEND_URL}/public/landing/${s("how_it_works", "image", "")}`}
                  alt="Como funciona"
                  style={{ width: "100%", display: "block" }}
                />
              </div>
            ) : (
              <div className="lp-how__visual lp-anim" data-d="3">
                <div className="lp-how__visual-top">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,.8)" strokeWidth="2" strokeLinecap="round">
                    <rect x="3" y="3" width="18" height="18" rx="2"/><path d="M8 7v7"/><path d="M12 7v4"/><path d="M16 7v10"/>
                  </svg>
                  <div className="lp-how__visual-title">Pipeline de Vendas — Kanban</div>
                </div>
                <div className="lp-kanban">
                  {[
                    { label: "Novo Lead", color: "#60a5fa", cards: [{ n: "Bruno F.", t: "WhatsApp", tc: "#dbeafe", tct: "#1d4ed8" }, { n: "Laura M.", t: "Orgânico", tc: "#E6F7FF", tct: "#0F766E" }] },
                    { label: "Qualificado", color: "#60A5FA", cards: [{ n: "Felipe S.", t: "Pro", tc: "#E6F7FF", tct: "#1D4ED8" }] },
                    { label: "Proposta", color: "#f59e0b", cards: [{ n: "Carla R.", t: "Urgente", tc: "#fef3c7", tct: "#92400e" }] },
                    { label: "Fechado", color: "#34d399", cards: [{ n: "Daniel W.", t: "Pago", tc: "#d1fae5", tct: "#065f46" }] },
                  ].map((col, ci) => (
                    <div key={ci} className="lp-kanban__col">
                      <div className="lp-kanban__col-h" style={{ color: col.color }}>{col.label}</div>
                      {col.cards.map((card, cj) => (
                        <div key={cj} className="lp-kanban__card">
                          <div className="lp-kanban__card-n">{card.n}</div>
                          <div className="lp-kanban__card-t" style={{ background: card.tc, color: card.tct }}>{card.t}</div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ── INTEGRATIONS ── */}
      <section className={`lp-sec${s("integrations", "bgColor", "") ? "" : " lp-sec--dark"}`} style={s("integrations", "bgColor", "") ? { background: s("integrations", "bgColor", "") } : undefined} id="integrations">
        <div className="lp-wrap">
          <div className="lp-anim" style={{ textAlign: "center" }}>
            {s("integrations", "eyebrow", "INTEGRAÇÕES") && <div className="lp-tag" style={{
              ...(s("integrations", "tagBgColor", "") ? { background: s("integrations", "tagBgColor", ""), borderColor: s("integrations", "tagBgColor", "") } : {}),
              ...(s("integrations", "tagColor", "") ? { color: s("integrations", "tagColor", "") } : {}),
            }}>{s("integrations", "eyebrow", "Integrações")}</div>}
          </div>
          <h2 className="lp-h2 lp-anim" data-d="1">{s("integrations", "sectionTitle", "Conectado a todos os canais")}</h2>
          <p className="lp-sub lp-anim" data-d="2">{s("integrations", "sectionSub", "Integre com as ferramentas que você já usa e centralize tudo em um único lugar.")}</p>
          <div className="lp-integ-grid">
            {integrations.map((item, i) => (
              <div
                key={i}
                className="lp-integ-card lp-anim"
                data-d={String((i % 3) + 1)}
                style={{
                  background: item.cardBg || s("integrations", "cardBgColor", "") || undefined,
                  borderColor: item.cardBorder || s("integrations", "cardBorderColor", "") || undefined,
                }}
              >
                <div className="lp-integ-icon" style={{ background: item.bg || "rgba(37, 99, 235,.08)" }}>{item.icon}</div>
                <h3 style={{ color: item.titleColor || s("integrations", "titleColor", "") || undefined }}>{item.title}</h3>
                <p style={{ color: item.descColor || s("integrations", "cardDescColor", "") || undefined }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section id="testimonials" className="lp-sec" style={{ background: s("testimonials_meta", "bgColor", "#f8f7ff") }}>
        <div className="lp-wrap lp-testi-section">
          {s("testimonials_meta", "eyebrow", "DEPOIMENTOS") && <div className="lp-tag lp-anim" style={{
              ...(s("testimonials_meta", "tagBgColor", "") ? { background: s("testimonials_meta", "tagBgColor", ""), borderColor: s("testimonials_meta", "tagBgColor", "") } : {}),
              ...(s("testimonials_meta", "tagColor", "") ? { color: s("testimonials_meta", "tagColor", "") } : {}),
            }}>{s("testimonials_meta", "eyebrow", "Depoimentos")}</div>}
          <h2 className="lp-h2 lp-anim" data-d="1">{s("testimonials_meta", "sectionTitle", "O que nossos clientes dizem")}</h2>
          <p className="lp-sub lp-anim" data-d="2">{s("testimonials_meta", "sectionSub", "Histórias reais de empresas que transformaram seu atendimento.")}</p>

          {testimonials.length > 0 && (
            <>
              <div className="lp-testi-grid lp-anim" data-d="3">
                {testimonials.map((t, i) => (
                  <div
                    key={i}
                    className={`lp-testi-card${i === testiIdx % testimonials.length ? " lp-testi-card--active" : ""}`}
                    style={{
                      ...(s("testimonials_meta", "cardBgColor", "") ? { background: s("testimonials_meta", "cardBgColor", "") } : {}),
                      ...(s("testimonials_meta", "cardBorderColor", "") ? { borderColor: s("testimonials_meta", "cardBorderColor", "") } : {}),
                    }}
                    onClick={() => setTestiIdx(i)}
                  >
                    <span className="lp-testi-quote-mark">"</span>
                    <div className="lp-testi-stars" style={s("testimonials_meta", "starColor", "") ? { color: s("testimonials_meta", "starColor", "") } : undefined}>{t.stars}</div>
                    <div className="lp-testi-quote" style={s("testimonials_meta", "quoteColor", "") ? { color: s("testimonials_meta", "quoteColor", "") } : undefined}>{t.quote}</div>
                    <div className="lp-testi-author">
                      {t.photo
                        ? <img src={`${process.env.REACT_APP_BACKEND_URL}/public/landing/${t.photo}`} alt={t.name} className="lp-testi-av" style={{ objectFit: "cover" }} />
                        : <div className="lp-testi-av" style={{ background: `linear-gradient(135deg, ${t.color}, #38bdf8)` }}>{t.init}</div>
                      }
                      <div>
                        <div className="lp-testi-name">{t.name}</div>
                        <div className="lp-testi-role">{t.role}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {testimonials.length > 1 && (
                <div className="lp-testi-dots">
                  {testimonials.map((_, i) => (
                    <button
                      key={i}
                      className={`lp-testi-dot${i === testiIdx % testimonials.length ? " lp-testi-dot--active" : ""}`}
                      style={i === testiIdx % testimonials.length ? { background: s("testimonials_meta", "accentColor", "#2563EB") } : undefined}
                      onClick={() => setTestiIdx(i)}
                      aria-label={`Depoimento ${i + 1}`}
                    />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* ── PRICING ── */}
      {plans.length > 0 && (
        <section className="lp-sec lp-sec--bg" id="pricing" style={s("pricing", "bgColor", "") ? { background: s("pricing", "bgColor", "") } : undefined}>
          <div className="lp-wrap">
            <div className="lp-anim" style={{ textAlign: "center" }}>
              {s("pricing", "eyebrow", "PLANOS") && <div className="lp-tag" style={{
                ...(s("pricing", "tagBgColor", "") ? { background: s("pricing", "tagBgColor", ""), borderColor: s("pricing", "tagBgColor", "") } : {}),
                ...(s("pricing", "tagColor", "") ? { color: s("pricing", "tagColor", "") } : {}),
              }}>{s("pricing", "eyebrow", "Planos")}</div>}
            </div>
            <h2 className="lp-h2 lp-anim" data-d="1">{s("pricing", "sectionTitle", "Preço justo para cada fase")}</h2>
            <p className="lp-sub lp-anim" data-d="2">{s("pricing", "sectionSub", "Comece com o que precisa e evolua conforme seu negócio cresce.")}</p>
            <div className="lp-price-grid">
              {[...plans]
                .filter(p => p.isPublic !== false)
                .sort((a, b) => parseFloat(a.amount) - parseFloat(b.amount))
                .map((plan, i, arr) => {
                  const feat = !!plan.isFeatured;
                  const mid = Math.floor(arr.length / 2);
                  const accentColor = s("pricing", "accentColor", "#2563EB");
                  const featBgFrom = s("pricing", "featBgFrom", "#2563EB");
                  const featBgTo = s("pricing", "featBgTo", "#2563eb");
                  const planItems = [
                    { label: plan.users > 0 ? `${plan.users} atendente(s)` : "Atendentes ilimitados", on: true },
                    { label: plan.connections > 0 ? `${plan.connections} conexão(ões) WhatsApp` : "Conexões ilimitadas", on: true },
                    { label: plan.queues > 0 ? `${plan.queues} fila(s)` : "Filas ilimitadas", on: true },
                    { label: "WhatsApp", on: !!plan.useWhatsapp },
                    { label: "Facebook", on: !!plan.useFacebook },
                    { label: "Instagram", on: !!plan.useInstagram },
                    { label: "Meta Ads", on: !!plan.useMetaAds },
                    { label: "Google Ads", on: !!plan.useGoogleAds },
                    { label: "Google Meu Negócio", on: !!plan.useGoogleBusiness },
                    { label: "E-mail", on: !!plan.useEmail },
                    { label: "Chat", on: !!plan.useChat },
                    { label: "Campanhas em massa", on: !!plan.useCampaigns },
                    { label: "Agendamentos", on: !!plan.useSchedules },
                    { label: "Chat interno", on: !!plan.useInternalChat },
                    { label: "API & Webhooks", on: !!plan.useExternalApi },
                    { label: "Kanban CRM", on: !!plan.useKanban },
                    { label: "Integrações", on: !!plan.useIntegrations },
                    { label: "Agente de IA", on: !!plan.useOpenAi },
                    plan.aiCreditsLimit > 0 ? { label: `${plan.aiCreditsLimit} créditos de IA/mês`, on: true } : null,
                    { label: "Produtos", on: !!plan.useProdutos },
                    { label: "Serviços", on: !!plan.useServicos },
                    { label: "Ordem de serviço", on: !!plan.useOrdemDeServico },
                    plan.nfLimit > 0 ? { label: `${plan.nfLimit} notas fiscais/mês`, on: true } : null,
                  ].filter(Boolean);
                  const cardStyle = feat
                    ? { background: `linear-gradient(145deg, ${featBgFrom} 0%, ${featBgTo} 100%)`, borderColor: "transparent" }
                    : {
                        ...(s("pricing", "cardBgColor", "") ? { background: s("pricing", "cardBgColor", "") } : {}),
                        ...(s("pricing", "cardBorderColor", "") ? { borderColor: s("pricing", "cardBorderColor", "") } : {}),
                      };
                  return (
                    <div key={plan.id} className={`lp-price-card lp-anim${feat ? " lp-price-card--feat" : ""}`} data-d={String(i + 1)} style={cardStyle}>
                      {feat && (
                        <div className="lp-price-badge" style={{
                          ...(s("pricing", "badgeBgColor", "") ? { background: s("pricing", "badgeBgColor", "") } : {}),
                          ...(s("pricing", "badgeTextColor", "") ? { color: s("pricing", "badgeTextColor", "") } : {}),
                        }}>
                          {s("pricing", "badgeText", "Mais popular")}
                        </div>
                      )}
                      <div className="lp-price-name" style={!feat && s("pricing", "textColor", "") ? { color: s("pricing", "textColor", "") } : undefined}>{plan.name}</div>
                      <div className="lp-price-tagline">
                        {plan.description || (plan.users > 0 ? `Para times de até ${plan.users} pessoas` : "Operação sem limite")}
                      </div>
                      <div className="lp-price-amount" style={!feat && s("pricing", "textColor", "") ? { color: s("pricing", "textColor", "") } : undefined}>
                        R$ {parseFloat(plan.amount).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                        <span className="lp-price-suffix">/mês</span>
                      </div>
                      <hr className="lp-price-div" />
                      <ul className="lp-price-items">
                        {planItems.map((item, j) => (
                          <li key={j} className={`lp-price-item${!item.on ? " lp-price-item--off" : feat ? " lp-price-item--on-w" : ""}`}>
                            {item.on
                              ? <CheckDark color={feat ? "#fff" : accentColor} />
                              : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={s("pricing", "disabledIconColor", "#d1d5db")} strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>}
                            {item.label}
                          </li>
                        ))}
                      </ul>
                      <button
                        className={`lp-price-btn ${feat ? "lp-price-btn--white" : i < mid ? "lp-price-btn--outline" : "lp-price-btn--primary"}`}
                        style={!feat ? {
                          ...(i < mid ? { color: accentColor, borderColor: accentColor + "66" } : { background: `linear-gradient(135deg, ${accentColor}, ${featBgTo})` }),
                        } : undefined}
                        onClick={() => comprarPlano(plan)}
                      >
                        {plan.buttonText || (feat ? s("pricing", "featBtnText", "Começar agora →") : s("pricing", "normalBtnText", "Assinar plano"))}
                      </button>
                    </div>
                  );
                })}
            </div>
          </div>
        </section>
      )}

      {/* ── BLOG ── */}
      <section id="blog" className="lp-sec lp-blog" style={{ display: blogPosts.length === 0 ? "none" : undefined, ...(s("blog", "bgColor", "") ? { background: s("blog", "bgColor", "") } : {}) }}>
          <div className="lp-wrap">
            <div style={{ textAlign: "center" }}>
              {s("blog", "sectionTag", "Blog") && (
                <div className="lp-tag lp-anim" style={{
                  ...(s("blog", "tagBgColor", "") ? { background: s("blog", "tagBgColor", ""), borderColor: s("blog", "tagBgColor", "") } : {}),
                  ...(s("blog", "tagColor", "") ? { color: s("blog", "tagColor", "") } : {}),
                }}>{s("blog", "sectionTag", "Blog")}</div>
              )}
              <h2 className="lp-h2 lp-anim" data-d="1">{s("blog", "sectionTitle", "Últimos artigos")}</h2>
              <p className="lp-sub lp-anim" data-d="2">{s("blog", "sectionSub", "Dicas, novidades e tendências sobre atendimento e CRM.")}</p>
            </div>
            <div className="lp-blog__carousel-wrap lp-anim" data-d="3">
              <button
                className="lp-blog__arrow lp-blog__arrow--prev"
                onClick={() => setBlogIdx(i => Math.max(0, i - 1))}
                disabled={blogIdx === 0}
                aria-label="Anterior"
              >&#8249;</button>

              <div
                className="lp-blog__viewport"
                style={{ cursor: 'grab' }}
                onMouseDown={e => { blogDragX.current = e.clientX; blogDragged.current = false; }}
                onMouseUp={e => {
                  if (blogDragX.current === null) return;
                  const diff = blogDragX.current - e.clientX;
                  blogDragX.current = null;
                  if (Math.abs(diff) > 50) {
                    blogDragged.current = true;
                    setBlogIdx(i => diff > 0 ? Math.min(blogMaxIdx, i + 1) : Math.max(0, i - 1));
                  }
                }}
                onMouseLeave={() => { blogDragX.current = null; }}
                onTouchStart={e => { blogDragX.current = e.touches[0].clientX; blogDragged.current = false; }}
                onTouchEnd={e => {
                  if (blogDragX.current === null) return;
                  const diff = blogDragX.current - e.changedTouches[0].clientX;
                  blogDragX.current = null;
                  if (Math.abs(diff) > 50) {
                    blogDragged.current = true;
                    setBlogIdx(i => diff > 0 ? Math.min(blogMaxIdx, i + 1) : Math.max(0, i - 1));
                  }
                }}
              >
                <div className="lp-blog__track" style={{ transform: blogTransform }}>
                  {blogPosts.map(post => (
                    <div
                      key={post.id}
                      className="lp-blog__card"
                      onClick={() => { if (!blogDragged.current) history.push(`/blog/${post.slug}`); }}
                      role="article"
                      style={{
                        ...(s("blog", "cardBgColor", "") ? { background: s("blog", "cardBgColor", "") } : {}),
                        ...(s("blog", "cardBorderColor", "") ? { borderColor: s("blog", "cardBorderColor", "") } : {}),
                      }}
                    >
                      {post.imagemCapa
                        ? <img
                            src={`${process.env.REACT_APP_BACKEND_URL}/public/${post.imagemCapa}`}
                            alt={post.titulo}
                            className="lp-blog__img"
                            onError={e => {
                              const cur = e.currentTarget.src;
                              const fallback = cur.replace('/blog/', '/');
                              if (cur !== fallback) { e.currentTarget.src = fallback; }
                              else { e.currentTarget.style.display = "none"; e.currentTarget.insertAdjacentHTML("afterend", '<div class="lp-blog__img--placeholder"></div>'); }
                            }}
                          />
                        : <div className="lp-blog__img--placeholder" />
                      }
                      <div className="lp-blog__body">
                        <div className="lp-blog__date" style={s("blog", "dateColor", "") ? { color: s("blog", "dateColor", "") } : undefined}>
                          {new Date(post.createdAt).toLocaleDateString("pt-BR", { day: "2-digit", month: "long", year: "numeric" })}
                        </div>
                        <div className="lp-blog__title" style={s("blog", "titleColor", "") ? { color: s("blog", "titleColor", "") } : undefined}>{post.titulo}</div>
                        {post.resumo && <div className="lp-blog__resumo" style={s("blog", "resumoColor", "") ? { color: s("blog", "resumoColor", "") } : undefined}>{post.resumo}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <button
                className="lp-blog__arrow lp-blog__arrow--next"
                onClick={() => setBlogIdx(i => Math.min(blogMaxIdx, i + 1))}
                disabled={blogIdx >= blogMaxIdx}
                aria-label="Próximo"
              >&#8250;</button>

              {blogMaxIdx > 0 && (
                <div className="lp-blog__dots">
                  {Array.from({ length: blogMaxIdx + 1 }, (_, i) => (
                    <button
                      key={i}
                      className={`lp-blog__dot${i === blogIdx ? ' lp-blog__dot--active' : ''}`}
                      onClick={() => setBlogIdx(i)}
                      aria-label={`Slide ${i + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>

      {/* ── FAQ ── */}
      <section className="lp-sec" id="faq" style={s("faq_meta", "bgColor", "") ? { background: s("faq_meta", "bgColor", "") } : undefined}>
        <div className="lp-wrap">
          <div className="lp-anim" style={{ textAlign: "center" }}>
            {s("faq_meta", "eyebrow", "DÚVIDAS") && <div className="lp-tag" style={{
              ...(s("faq_meta", "tagBgColor", "") ? { background: s("faq_meta", "tagBgColor", ""), borderColor: s("faq_meta", "tagBgColor", "") } : {}),
              ...(s("faq_meta", "tagColor", "") ? { color: s("faq_meta", "tagColor", "") } : {}),
            }}>{s("faq_meta", "eyebrow", "Dúvidas")}</div>}
          </div>
          <h2 className="lp-h2 lp-anim" data-d="1">{s("faq_meta", "sectionTitle", "Perguntas frequentes")}</h2>
          <p className="lp-sub lp-anim" data-d="2">{s("faq_meta", "sectionSub", "Tudo que você precisa saber antes de começar.")}</p>
          <div className="lp-faq-list lp-anim" data-d="3">
            {faqs.map((item, i) => (
              <div
                key={i}
                className={`lp-faq-item${openFaq === i ? " lp-faq-item--open" : ""}`}
                style={{
                  ...(s("faq_meta", "cardBgColor", "") ? { background: s("faq_meta", "cardBgColor", "") } : {}),
                  ...(s("faq_meta", "cardBorderColor", "") ? { borderColor: openFaq === i ? s("faq_meta", "accentColor", s("faq_meta", "cardBorderColor", "")) : s("faq_meta", "cardBorderColor", "") } : {}),
                }}
              >
                <button
                  className="lp-faq-btn"
                  style={s("faq_meta", "titleColor", "") ? { color: s("faq_meta", "titleColor", "") } : undefined}
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  {item.q}
                  <svg
                    className={`lp-faq-chev${openFaq === i ? " lp-faq-chev--open" : ""}`}
                    width="18" height="18" viewBox="0 0 24 24" fill="none"
                    stroke={openFaq === i ? s("faq_meta", "accentColor", "#2563EB") : "currentColor"}
                    strokeWidth="2.5" strokeLinecap="round"
                  >
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                </button>
                {openFaq === i && (
                  <div
                    className="lp-faq-body"
                    style={s("faq_meta", "bodyColor", "") ? { color: s("faq_meta", "bodyColor", "") } : undefined}
                  >{item.a}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <div id="cta" className="lp-cta" style={s("cta", "gradFrom", "") ? { background: `linear-gradient(135deg, ${s("cta", "gradFrom", "#2563EB")} 0%, ${s("cta", "gradTo", "#2563eb")} 100%)` } : undefined}>
        <div className="lp-cta__inner">
          <div className="lp-cta__eyebrow">{s("cta", "eyebrow", "Comece hoje")}</div>
          <h2 className="lp-cta__title" style={s("cta", "textColor", "") ? { color: s("cta", "textColor", "") } : undefined}>
            {s("cta", "title", "Transforme seu atendimento")}{appName ? <><br />com {appName}</> : ""}
          </h2>
          <p className="lp-cta__sub">
            {s("cta", "sub", "Centralize canais, automatize com IA e gerencie toda sua equipe. Sem complexidade.")}
          </p>
          <button
            className="lp-cta__btn"
            style={{
              ...(s("cta", "btnTextColor", "") ? { color: s("cta", "btnTextColor", "") } : {}),
              ...(s("cta", "btnBgColor", "") ? { background: s("cta", "btnBgColor", ""), boxShadow: "none" } : {}),
            }}
            onMouseEnter={e => {
              if (s("cta", "btnHoverBgColor", ""))   e.currentTarget.style.background = s("cta", "btnHoverBgColor", "");
              if (s("cta", "btnHoverTextColor", "")) e.currentTarget.style.color      = s("cta", "btnHoverTextColor", "");
            }}
            onMouseLeave={e => {
              e.currentTarget.style.background = s("cta", "btnBgColor", "") || "";
              e.currentTarget.style.color      = s("cta", "btnTextColor", "") || "";
            }}
            onClick={irParaPlanos}
          >
            {s("cta", "btn", "Criar conta gratuita →")}
          </button>
          <div className="lp-cta__trust">
            {[
              s("cta", "trust1", "Sem cartão de crédito"),
              s("cta", "trust2", "Suporte em português"),
              s("cta", "trust3", "Cancele quando quiser"),
            ].filter(Boolean).map((t, i) => (
              <div key={i} className="lp-cta__trust-item">
                <div className="lp-cta__trust-dot">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </div>
                {t}
              </div>
            ))}
          </div>
        </div>
      </div>

      <LandingFooter appName={appName} />

      <CookieBanner sectionData={secData.cookies} />

      <VLibras />

      {/* ── WA FLOAT ── */}
      <a href={"https://wa.me/" + waNumber} target="_blank" rel="noopener noreferrer" className="lp-wa" title="Fale conosco">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="#fff">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>
    </div>
  );
}
